import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { chartTheme } from "../lib/chart.ts";
import { useThemeTick } from "./chrome.tsx";
import type {
  BrandReport,
  DealSlice,
  ExportDataset,
  Filters,
  LeadSlice,
  OperationsReport,
  PipelineReport,
} from "../lib/api.ts";
import { api } from "../lib/api.ts";
import type { DeltaFn } from "../lib/compare.ts";
import { BRAND_COLOR, compact, dec, money, num, pct, pctValue } from "../lib/format.ts";
import { Card, DrillNum, Kpi } from "./widgets.tsx";
import { useDrillOpener, type Drill } from "./dealList.tsx";
import { DepositReviewBlock } from "./depositReview.tsx";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { ATTENTION, CHANNEL, DATASET, SENTINEL, UI } from "../i18n/dict.ts";

/**
 * Экраны, закрывающие оставшиеся услуги КП-2: мультибрендовый операционный
 * отчёт, аналитика конверсии пайплайна, отслеживание сделок и депозитов и
 * выгрузка данных.
 *
 * Общее правило для всех четырёх: ни одна цифра не должна выглядеть
 * самоочевидной. У каждой либо подпись с определением, либо знаменатель рядом —
 * иначе первый же вопрос «а что это значит» превращает экран в спор.
 */

/**
 * Проза этих экранов. Один блок в начале файла — см. App.tsx.
 *
 * Длинные примечания разрезаны на куски там, где внутрь предложения попадает
 * посчитанное число: цифру нельзя вписать в перевод, иначе она однажды разойдётся
 * с данными. Порядок кусков в русском и английском совпадает — это условие того,
 * что одна и та же разметка печатает оба языка.
 */
const TX = {
  /* --- мультибренд: строка сверки трёх итогов
   *
   * Слово «ещё» из прежней формулировки читалось как «сверх»: «182 лида не
   * несут бренда», а ниже «243 лидов нет ЕЩЁ и парка» — и заказчик увидел
   * противоречие. Противоречия не было, была вложенность: `core.dim_park.brand_key`
   * объявлен NOT NULL, поэтому park_key ≠ NULL влечёт brand_key ≠ NULL, и лидов
   * без парка всегда не меньше, чем без бренда. Замер за неделю: 1165 лидов,
   * 179 без бренда, 239 без парка, 60 с брендом но без парка, и НИ ОДНОГО с
   * парком без бренда — по всем 38 месяцам истории ни одного.
   *
   * Поэтому текст теперь: общий итог печатается один раз (он же знаменатель
   * «карточки покрывают X из Y», он же цифра Обзора), названо число, которое
   * попало в карточку но не в строку таблицы — раньше разницу 993→932 читатель
   * домысливал сам, — и равенство выписано явно, чтобы «не добавка» было видно
   * арифметикой, а не обещанием.
   */
  /* Существительное стоит ДО числа во всех четырёх кусках: «у 61 лидов» — это
     ошибка согласования, а числа тут любые, от 1 до тысяч, и падеж после них
     русский меняет. Двоеточие снимает вопрос без таблицы форм. */
  reconNoBrandLabel: { ru: "Лидов без бренда:", en: "Leads with no brand:" },
  reconNoBrandWhy: {
    ru: "— в CRM не сказано, к какому парку или бренду они относятся.",
    en: "— nothing in the CRM says which park or brand they belong to.",
  },
  reconCardsCover: { ru: "Карточки брендов покрывают", en: "The brand cards cover" },
  reconLeadsInPeriod: { ru: "лидов за этот период.", en: "leads in this period." },
  reconTableCovers: { ru: "Таблица парков покрывает", en: "The park table covers" },
  reconChannelKnowsBrand: {
    ru: "Лидов, у которых канал знает бренд, но не парк:",
    en: "Leads whose channel knows the brand but not the park:",
  },
  reconNoParkLabel: { ru: "Лиды без парка:", en: "Leads with no park:" },
  reconSumIncludes: {
    ru: "Это не добавка к первым, а те же лиды плюс эти.",
    en: "Not an addition to the first figure, but the same leads plus these.",
  },
  /* Исключённое называется вслух и открывается списком: молча выброшенные
     записи — тот же дефект, что и записи, попавшие не туда. */
  reconTicketsExcluded: {
    ru: "Покупки билетов с сайта здесь за лиды не считаются: интеграция создаёт их сразу закрытыми, парка в CRM у них нет, а деньги за билет приходят из базы сайта. Исключено покупок:",
    en: "Ticket purchases from the site are not counted as leads here: the integration creates them already closed, they carry no park in the CRM, and the money arrives from the ticketing database. Purchases excluded:",
  },
  drillTicketPurchases: { ru: "Покупки билетов с сайта", en: "Ticket purchases from the site" },

  /* --- мультибренд: карточка бренда */
  parksCount: {
    one: { ru: "парк", en: "park" },
    few: { ru: "парка", en: "parks" },
    many: { ru: "парков", en: "parks" },
  },
  hideParks: { ru: "Скрыть парки", en: "Hide parks" },
  posRevenue: { ru: "Касса", en: "POS revenue" },
  noSyrveAccess: { ru: "нет доступа к Syrve", en: "no Syrve access" },
  tillNotAvailable: {
    ru: "за этот период по бренду нет данных кассы",
    en: "till data not available for this brand in this period",
  },
  /* Английская форма одна на все три: множественное число различает только русский. */
  parksNoAccessAtAll: {
    one: { ru: "парк вовсе без доступа к Syrve", en: "park has no Syrve access at all" },
    few: { ru: "парка вовсе без доступа к Syrve", en: "parks have no Syrve access at all" },
    many: { ru: "парков вовсе без доступа к Syrve", en: "parks have no Syrve access at all" },
  },
  parksReporting: {
    one: { ru: "парка отчитывается", en: "parks reporting" },
    few: { ru: "парков отчитываются", en: "parks reporting" },
    many: { ru: "парков отчитываются", en: "parks reporting" },
  },
  withNoSyrveAccess: { ru: "без доступа к Syrve", en: "with no Syrve access" },
  ticketsSold: { ru: "билетов продано", en: "tickets sold" },
  whatTillTook: { ru: "Что взяла касса", en: "What the till took" },
  onTickets: { ru: "на билетах", en: "tickets" },
  onFood: { ru: "на еде", en: "food" },
  onBanquet: { ru: "в чеках праздников", en: "in party checks" },
  /* Чеки праздников — чеки целиком, а не слагаемое состава (миграция 090):
     еда и билеты, пробитые на празднике, стоят и в своих категориях. */
  partyChecksOverlap: {
    ru: "чеки праздников целиком, их еда и билеты учтены и в своих строках",
    en: "party checks are whole checks; their food and tickets are counted in their own lines too",
  },
  qualifiedLower: { ru: "квалифицированных", en: "qualified" },
  banquetDeals: { ru: "Банкетные сделки", en: "Banquet deals" },
  wonLower: { ru: "завершено", en: "completed" },
  /* Не «из M созданных»: с 24.09.2026 завершённые считаются по дате праздника, а
     созданные — по дате создания, и «8 из 20» читалось бы как доля, которой нет. */
  createdInPeriod: { ru: "сделок создано за период", en: "deals created in the period" },
  inCrm: { ru: "в CRM", en: "in CRM" },
  against: { ru: ", против", en: ", against" },
  ofBanquetMoneyAtTill: {
    ru: "AED в чеках праздников по кассе — те же праздники, складывать нельзя",
    en: "AED in party checks at the till — the same parties, never add the two",
  },
  exceptionsApplied: {
    one: { ru: "исключение сверки", en: "reconciliation exception" },
    few: { ru: "исключения сверки", en: "reconciliation exceptions" },
    many: { ru: "исключений сверки", en: "reconciliation exceptions" },
  },
  drillExceptions: { ru: "Исключения сверки", en: "Reconciliation exceptions" },
  hintExceptions: {
    ru: "записи, по которым праздники периода посчитаны не так, как стоит в CRM",
    en: "records that make this period's parties differ from the CRM",
  },
  onlineTickets: { ru: "Онлайн-билеты", en: "Online tickets" },
  paidBookingsOnSite: {
    ru: "оплаченных броней на нашем сайте · в кассу выше не входят",
    en: "paid bookings on our site · not included in POS revenue above",
  },
  filterToThisPark: {
    ru: "Отфильтровать весь дашборд по этому парку",
    en: "Filter the whole dashboard to this park",
  },
  noAccess: { ru: "нет доступа", en: "no access" },
  colCompleted: { ru: "Завершено", en: "Completed" },
  colPosAed: { ru: "Касса, AED", en: "POS, AED" },

  /* --- мультибренд: графики и сводная таблица */
  leadsByMonth: { ru: "Обращения по месяцам", en: "Inquiries by month" },
  qualifiedByMonth: {
    ru: "Квалифицированные лиды по месяцам",
    en: "Qualified leads by month",
  },
  /* Определение шире, чем «дошли до менеджера»: с 18.09.2026 сюда входят и
     завершившиеся праздником минуя обе стадии — ровно как на Обзоре. Пока
     подпись говорила только про менеджера, одно имя значило на двух экранах
     разное. */
  qualifiedLeadsHint: {
    ru: "побывали на Interested или Park Tour, либо завершились праздником",
    en: "were at Interested or Park Tour, or ended in a completed party",
  },
  perBrand: { ru: "по брендам", en: "per brand" },
  posRevenueByMonth: { ru: "Касса по месяцам", en: "POS revenue by month" },
  aedPerBrand: { ru: "AED, по брендам", en: "AED, per brand" },
  streamAll: { ru: "Всё", en: "Everything" },
  streamBanquet: { ru: "Только банкеты", en: "Banquets only" },
  streamTickets: { ru: "Только билеты", en: "Tickets only" },
  streamHint: {
    ru: "колонки другого потока скрыты, сами числа не изменились",
    en: "the other stream's columns are hidden, the numbers themselves are unchanged",
  },
  allParksSideBySide: { ru: "Все парки рядом", en: "All parks side by side" },
  colQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  colDealsWon: { ru: "Завершённые праздники", en: "Completed events" },
  colBanquetsCrm: { ru: "Банкеты в CRM, AED", en: "Banquets in CRM, AED" },
  colPosRevenueAed: { ru: "Касса, AED", en: "POS revenue, AED" },
  colOnlineTicketsAed: { ru: "Онлайн-билеты, AED", en: "Online tickets, AED" },
  colTicketsSold: { ru: "Билетов продано", en: "Tickets sold" },
  allParksNote: {
    ru: "Всё пересчитано в AED, чтобы бренды можно было сравнивать: Leo & Loona стоит и в ОАЭ, и в Бахрейне, а дирхамы к динарам не прибавляются. Касса и банкетные суммы CRM намеренно стоят рядом и никогда не складываются: банкетные деньги частично проходят через кассу, и сумма дала бы двойной счёт.",
    en: "Everything is converted to AED so brands can be compared — Leo & Loona spans both the UAE and Bahrain, and dirhams cannot be added to dinars. POS revenue and CRM banquet amounts are deliberately shown side by side and never summed: banquet money partly goes through the till, so adding them would double-count.",
  },

  /* --- единицы времени. Само число не переводится и не переформатируется. */
  unitMin: { ru: "мин", en: "min" },
  unitHours: { ru: "ч", en: "h" },
  unitDays: { ru: "дн.", en: "days" },

  /* --- пайплайн: итог периода */
  securedDeposits: { ru: "Обеспеченные депозиты", en: "Secured deposits" },
  byEventDate: { ru: "по дате праздника", en: "by event date" },
  aedTakenOutOf: { ru: "AED получено из", en: "AED taken, out of" },
  ofBanquetValue: { ru: "AED стоимости банкетов", en: "AED of banquet value" },
  closedDeposits: { ru: "Закрытые депозиты", en: "Closed deposits" },
  bySaleDate: {
    ru: "по дате оплаты депозита, где бы ни был сам банкет",
    en: "by deposit payment date, wherever the banquet falls",
  },
  aedTaken: { ru: "AED получено", en: "AED taken" },
  completedEvents: { ru: "Завершённые праздники", en: "Completed events" },
  colAvgParty: { ru: "Средний чек", en: "Average per party" },
  avgPerPartyLower: { ru: "средний чек на праздник", en: "average per party" },
  eventsCompletedCounted: {
    one: { ru: "праздник завершён", en: "event completed" },
    few: { ru: "праздника завершено", en: "events completed" },
    many: { ru: "праздников завершено", en: "events completed" },
  },
  avgPerParty: { ru: "средний чек", en: "average per party" },
  footCompletedEvents: {
    ru: "банкеты, которые действительно прошли в этом периоде",
    en: "banquets that actually happened in this period",
  },
  completedRevenue: { ru: "Выручка завершённых", en: "Completed revenue" },
  footCompletedRevenue: {
    ru: "итоговые суммы этих банкетов, со всеми пробитыми допами",
    en: "final totals of those banquets, every add-on rung up",
  },
  depositsConfuseTitle: {
    ru: "Обеспеченные и закрытые депозиты легко спутать, и совпадают они редко.",
    en: "Secured deposits and Closed deposits are easy to confuse and rarely match.",
  },
  closedWord: { ru: "Закрытые", en: "Closed" },
  closedIsWhat: {
    ru: "— это то, что отдел продал в этом периоде, где бы ни оказался банкет.",
    en: "is what the team sold this period, wherever the banquet lands.",
  },
  securedWord: { ru: "Обеспеченные", en: "Secured" },
  securedIsWhat1: { ru: "— это то, что уже оплачено за", en: "is what is already paid for" },
  thisWord: { ru: "этот", en: "this" },
  securedIsWhat2: {
    ru: "период, когда бы продажа ни случилась.",
    en: "period, whenever it was sold.",
  },
  depositsMeasured: {
    ru: "Проще всего так: одно число про работу продавцов за период, другое — про праздники, которые в этом периоде пройдут.",
    en: "Put simply: one number is about what the sales team did in the period, the other about the events the period will host.",
  },

  /* --- пайплайн: когорта */
  /* Не «квалифицированные лиды»: здесь когорта ВХОДА на стадии в периоде, база двух
     конверсий справа. Сделки мимо стадий — отдельной, четвёртой плиткой (29.09.2026). */
  enteredStages: { ru: "Вошли в Interested / Park Tour", en: "Entered Interested / Park Tour" },
  enteredInterestedOrTour: {
    ru: "в этом периоде, включая лидов, созданных раньше",
    en: "in this period, including leads created earlier",
  },
  /*
   * Подписи говорят о СТАДИИ CRM и ни о чём больше.
   *
   * Прежние обещали физический приезд («побывали на осмотре парка», «got a park
   * tour», «так и не приехали», «never came in»), которого система не знает и
   * знать не может. Замер, трижды: (1) факта посещения парка в хранилище нет
   * вовсе — `core.v_customer_touch` даёт ровно prompt / inquiry /
   * reached_manager / deal_created / paid / won, и единственный признак тура —
   * стадия в CRM; (2) из 264 лидов когорты со стадией Park Tour 142 (53,8 %)
   * НИКОГДА не были на Interested, ещё у 13 тур случился РАНЬШЕ Interested —
   * ветки параллельны, а не последовательны, и тур предлагают не всем;
   * (3) 484 из 1027 лидов знаменателя стоят на Interested прямо сейчас, то есть
   * тур ещё возможен, а 138 уже дошли до сделки без тура — про них «так и не
   * приехали» просто ложно.
   *
   * Английский текст поэтому изменён вместе с русским: «got a park tour» и
   * «never came in» — это утверждения о реальности, а измерена стадия.
   */
  gotAParkTour: { ru: "дошли до стадии Park Tour", en: "reached the Park Tour stage" },
  dealFromInterested: { ru: "Сделка из Interested", en: "Deal from Interested" },
  ofLeadsNoParkTourStage: {
    ru: "лидов из Interested без стадии Park Tour",
    en: "of Interested leads with no Park Tour stage",
  },
  dealFromParkTour: { ru: "Сделка из Park Tour", en: "Deal from Park Tour" },
  ofLeadsMovedToParkTour: {
    ru: "лидов, доведённых до стадии Park Tour",
    en: "of leads moved to the Park Tour stage",
  },
  dealStraightFromInquiry: { ru: "Сделка сразу из Inquiry", en: "Deal straight from Inquiry" },
  skippedFunnelEntirely: { ru: "полностью минуя воронку", en: "skipped the funnel entirely" },
  alreadyCompleted: { ru: "уже завершены", en: "already completed" },
  /*
   * Имя стадии не переводится — оно жирным стоит первым куском в обоих языках,
   * а связка («— это» / «is») уехала внутрь примечания, потому что тире и глагол
   * в русском и английском стоят по-разному.
   */
  parkTourStageBold: { ru: "Стадия Park Tour", en: "The Park Tour stage" },
  parkTourNote: {
    ru: "— это стадия CRM и ничего больше: посещения парка хранилище не знает вовсе. Половина лидов на этой стадии никогда не была на Interested, а часть попала на неё раньше Interested — ветки параллельны, тур предлагают не каждому, поэтому две конверсии несравнимы и намеренно разведены. Третья цифра — другая когорта: лид, по которому появилась сделка, ни разу не побывав на Interested или Park Tour. Это не всегда небрежность — обращение по дню рождения через два дня закрывают по телефону, и вести его по воронке было бы театром.",
    en: "is a CRM stage and nothing more: a park visit is not a fact this warehouse holds. Half the leads on that stage were never on Interested, and some reached it before Interested — the branches are parallel, a tour is not offered to everyone, so the two conversion rates are not comparable and are deliberately kept apart. The third figure is a different cohort: a lead that got a deal without ever entering Interested or Park Tour. That is not always sloppiness — an inquiry for a birthday two days away is closed by phone, and walking it through the funnel would be theatre.",
  },

  /* --- пайплайн: скорость */
  firstResponse: { ru: "Время первого ответа", en: "First response time" },
  minutesShort: { ru: "мин", en: "min" },
  humansAlone: { ru: "только люди", en: "humans alone" },
  withoutAnyReply: { ru: "без ответа вовсе", en: "with no reply at all" },
  noConversationsInPeriod: {
    ru: "за период обращений в открытых линиях не было",
    en: "no open-line inquiries in this period",
  },
  leadToManager: { ru: "Лид → менеджер", en: "Lead → manager" },
  medianOf: { ru: "медиана по", en: "median of" },
  percentile90: { ru: "90-й процентиль", en: "90th percentile" },
  managerToDeal: { ru: "Менеджер → сделка создана", en: "Manager → deal created" },
  footMedianOnlyWithDeal: {
    ru: "медиана, только лиды, у которых появилась сделка",
    en: "median, only leads that produced a deal",
  },
  cohortDealRate: { ru: "Конверсия когорты в сделку", en: "Cohort deal rate" },
  leadsEnteredStages: {
    ru: "лидов, вошедших в Interested или Park Tour",
    en: "leads that entered Interested or Park Tour",
  },
  cohortNoteTitle: {
    ru: "Это не та когорта, что на Обзоре, и совпадать они не должны.",
    en: "This cohort is not the one on Overview, and the two are not supposed to match.",
  },
  cohortNote1: { ru: "Здесь это лиды, которые", en: "Here it is leads that" },
  enteredBold: { ru: "вошли в", en: "entered" },
  cohortNote2: {
    ru: "Interested или Park Tour в течение периода, включая созданные раньше, — для этого экран и нужен. Обзор считает лиды,",
    en: "Interested or Park Tour during the period, including leads created earlier — that is what this screen is for. Overview counts leads",
  },
  createdBold: { ru: "созданные", en: "created" },
  cohortNote3: {
    ru: "в периоде, и засчитывает их продвижение когда бы оно ни случилось, хоть месяцы спустя.",
    en: "in the period and credits their progress whenever it happens, even months later.",
  },

  /* --- пайплайн: разрезы */
  byChannel: { ru: "По каналам", en: "By channel" },
  whereCohortCameFrom: { ru: "откуда пришла когорта", en: "where the cohort came from" },
  byPark: { ru: "По паркам", en: "By park" },
  byManager: { ru: "По менеджерам", en: "By manager" },
  whoIsWorkingCohort: { ru: "кто ведёт когорту", en: "who is working the cohort" },
  managerNote1: { ru: "Когорта — это лиды, которые", en: "The cohort is leads that" },
  managerNote2: {
    ru: "Interested или Park Tour внутри периода, по истории стадий, — включая созданные много раньше. Считать по дате создания лида значило бы, что любой недавний период выглядит плохо: сделки выигрываются неделями позже. Этим же объясняется большой 90-й процентиль на «лид → менеджер» — поднятые старые лиды сидят в той же когорте.",
    en: "Interested or Park Tour inside the period, taken from stage history — including leads created much earlier. Counting by lead creation date instead would make any recent period look bad, because deals are won weeks later. That also explains a large 90th percentile for lead → manager: revived old leads sit in the same cohort.",
  },
  /* --- причины провала лидов (миграция 157) */
  lossTitle: { ru: "Причины провала лидов", en: "Why leads were lost" },
  lossHint: {
    ru: "лиды, закрытые в провальную стадию в периоде",
    en: "leads closed into a lost stage in the period",
  },
  lossReasonCol: { ru: "Причина", en: "Reason" },
  lossTotal: { ru: "Всего", en: "Total" },
  lossNoReason: { ru: "причина не указана", en: "no reason given" },
  lossRemoved: { ru: "убрана из списка", en: "removed from the list" },
  lossUnknown: { ru: "значение", en: "value" },
  lossStageGone: { ru: "стадии больше нет в портале", en: "no longer a stage in the portal" },
  lossNone: { ru: "в периоде проигранных лидов нет", en: "no leads were lost in the period" },
  lossNote: {
    ru: "Стадии и причины берутся из портала и обновляются раз в час: новая причина появится строкой сама, переименованная — с новым именем, убранная из списка останется у старых лидов. Дата — последний вход лида в его провальную стадию, а не дата создания: «что потеряли за неделю». В разбивку входят все лиды, а не только дошедшие до передачи в продажи, — большую часть закрывают прямо с INQUIRY. Любое число открывает список лидов.",
    en: "Stages and reasons come from the portal and refresh hourly: a new reason appears as a row on its own, a renamed one under its new name, and one removed from the list stays on older leads. The date is when the lead last entered its lost stage, not when it was created — “what we lost this week”. All leads count, not only those handed to sales: most are closed straight from INQUIRY. Any figure opens the list of leads.",
  },
  colName: { ru: "Название", en: "Name" },
  colReached: { ru: "Вошли в Interested / Park Tour", en: "Entered Interested / Park Tour" },
  colDeal: { ru: "Сделка", en: "Deal" },
  colConv: { ru: "Конв.", en: "Conv." },
  colRevenueAed: { ru: "Выручка, AED", en: "Revenue, AED" },

  /* --- сделки и депозиты
   *
   * Названия плиток берутся из `UI.depositRecorded` / `UI.depositConfirmed`, а
   * не живут здесь: одно и то же слово «депозит» стояло над двумя РАЗНЫМИ
   * фактами, и заказчик прочёл это как ошибку в расчёте. Замер: на 134 сделках,
   * известных обеим системам, CRM знает 192 206 AED, а брони — 170 204 AED,
   * то есть брони недосчитывают 11,4 %; ровно 1 000 AED стоит в 87 бронях из
   * 134, потому что система бронирования регистрирует ОДИН холд, а доплаты
   * депозита менеджер вносит только в CRM. Поэтому подпись обязана говорить,
   * ОТКУДА число, — иначе разница выглядит расхождением одной метрики.
   */
  byDealCreationDate: { ru: "по дате создания сделки", en: "by deal creation date" },
  dealsCarryDeposit: { ru: "сделок с депозитом", en: "deals carry a deposit" },
  andMore: { ru: ", и ещё", en: ", and" },
  paidWithoutCrmField: {
    ru: "оплачены, а поле в CRM осталось пустым",
    en: "more were paid without the CRM field being filled",
  },
  stillInPipeline: { ru: "AED ещё в воронке", en: "AED still in the pipeline" },
  upcomingEvents: { ru: "Ближайшие праздники", en: "Upcoming events" },
  nextWord: { ru: "следующие", en: "next" },
  daysNotFiltered: {
    ru: "дней, считая сегодня · фильтр периода выше не применяется",
    en: "days, today included · not filtered by the period above",
  },
  withoutPaidBooking: { ru: "без оплаченной брони", en: "without a paid booking" },
  /* «По дате праздника» панель пишет сама — здесь только то, чего она не знает. */
  hintUpcoming: {
    ru: "сделки не проигранные · период сверху не применяется, парки и бренды — да",
    en: "deals not lost · the period above does not apply, parks and brands do",
  },
  fromBookingSystem: {
    ru: "из системы бронирования, по дате создания брони",
    en: "from the booking system, by booking creation date",
  },
  bookings: { ru: "Брони", en: "Bookings" },
  paid: { ru: "Оплачено", en: "Paid" },
  collected: { ru: "Получено", en: "Collected" },
  paymentLinkSent: { ru: "Ссылка на оплату отправлена", en: "Payment link sent" },
  expired: { ru: "Истекло", en: "Expired" },
  cancelled: { ru: "Отменено", en: "Cancelled" },
  linkToPayment: { ru: "Ссылка → оплата", en: "Link → payment" },
  payments: { ru: "платежей", en: "payments" },
  withinAnHour: { ru: "в течение часа", en: "within an hour" },
  withinADay: { ru: "в течение суток", en: "within a day" },
  /* Название плитки цитируется через `UI.depositRecorded`, а не переписывается
     здесь: копия однажды разошлась бы с самой плиткой. Кавычки внутри куска,
     потому что в русском они «», а в английском “”. */
  whyDiffersFrom: { ru: "Почему это расходится с «", en: "Why this differs from “" },
  quoteClose: { ru: "».", en: "”." },
  neitherIsWrong: {
    ru: "Обе цифры верны — они просто считают разное, и вся разница видна ниже построчно.",
    en: "Both figures are correct — they simply count different things, and the whole difference is listed below.",
  },
  onThe: { ru: "на", en: "on the" },
  dealsBothSystemsKnow: {
    ru: "сделках, которые знают обе системы",
    en: "deals both systems know",
  },
  on: { ru: "на", en: "on" },
  dealsNoPaidBooking: {
    ru: "сделках без оплаченной брони в этом периоде",
    en: "deals with no paid booking in this period",
  },
  bookingSystem: { ru: "Система бронирования", en: "Booking system" },
  onTheSame: { ru: "на тех же", en: "on the same" },
  dealsWord: { ru: "сделках", en: "deals" },
  paymentsWhoseDeal: {
    ru: "платежах, чья сделка создана вне этого периода или не существует вовсе",
    en: "payments whose deal was created outside this period or does not exist at all",
  },
  bothSystemsNotEqual: {
    ru: "Два числа «обе системы» тоже не равны: на части сделок поле CRM больше платежа, на части меньше. Это пробел в заполнении CRM, а не в расчёте.",
    en: "The two “both systems” figures are not equal either: on some deals the CRM field holds more than the payment, on others less. That is a CRM-entry gap, not a calculation one.",
  },
  bitrixPrepaymentFlag: {
    ru: "В Битриксе есть признак «предоплата получена», но дата предоплаты пуста у всех сделок рабочей эпохи, поэтому время оплаты измеримо только в системе бронирования.",
    en: "Bitrix has a flag for “prepayment received” but its prepayment date is empty on every deal in the managed era, so payment timing can only be measured in the booking system.",
  },
  dealsTook: { ru: "сделок провели", en: "deals took" },
  aedThroughBookingSystem: {
    ru: "AED через систему бронирования, а поле депозита в CRM осталось пустым",
    en: "AED through the booking system while their CRM deposit field stayed empty",
  },
  moneyMissingFrom: {
    ru: "— эти деньги не попали в «",
    en: "— that money is missing from “",
  },
  moneyMissingFix: {
    ru: "», и починка — на стороне CRM.",
    en: "”, and the fix belongs in the CRM.",
  },
  morePaidThanLinks: {
    ru: "Оплаченных броней больше, чем отправленных ссылок, — их оплатили другим способом, поэтому время выше посчитано по",
    en: "More bookings are paid than had a link sent — those were paid another way, so the timing above covers",
  },
  needsAttention: { ru: "Требует внимания", en: "Needs attention" },
  eachRowFixable: {
    ru: "каждая строка — исправимое расхождение",
    en: "each row is a fixable discrepancy",
  },
  colIssue: { ru: "Проблема", en: "Issue" },
  colAmountAed: { ru: "Сумма, AED", en: "Amount, AED" },
  nothingToFix: { ru: "В этом периоде нечего исправлять", en: "Nothing to fix in this period" },
  depositOverAmountNote: {
    ru: "«Депозит больше суммы сделки» — это в основном Бахрейн, где стоимость сделки часто заглушка, а не итог банкета. Строка показана, а не исправлена молча: починка — на стороне CRM, а не в отчёте.",
    en: "“Deposit larger than the deal amount” is mostly Bahrain, where the deal value is often a placeholder rather than the banquet total. It is listed here rather than silently corrected: the fix belongs in the CRM, not in a report.",
  },

  /* --- выгрузка */
  fullReport: { ru: "Полный отчёт", en: "Full report" },
  everySummaryOneWorkbook: { ru: "все сводки в одной книге", en: "every summary in one workbook" },
  preparing: { ru: "Готовим…", en: "Preparing…" },
  downloadXlsx: { ru: "Скачать .xlsx", en: "Download .xlsx" },
  fullReportNote: {
    ru: "Каналы, выручка парков по дням, онлайн-билеты и депозиты отдельными листами, плюс лист с периодом и фильтрами, по которым файл собран, — чтобы цифры можно было объяснить и через месяц.",
    en: "Channels, park revenue by day, online tickets and deposits as separate tabs, plus a tab recording the period and filters the file was built with — so the numbers can still be explained a month from now.",
  },
  columns: { ru: "колонок", en: "columns" },
  exportsUseFilters: {
    ru: "Выгрузка использует фильтры, выбранные выше:",
    en: "Exports use the filters currently selected above:",
  },
  parkS: { ru: "парк(ов)", en: "park(s)" },
  allParks: { ru: ", все парки", en: ", all parks" },
  exportMoneyNote: {
    ru: "Деньги выгружаются и в валюте парка, и в AED, а время — дубайское: та же основа, что и на экранах.",
    en: "Money is exported both in the park’s own currency and in AED, and timestamps are in Dubai time — the same basis the dashboard uses.",
  },
  /* Добавлено при переходе на два языка: заголовки в файле остаются английскими,
     и это должно быть сказано на экране, а не только в отчёте о работе. */
  headersAlwaysEnglish: {
    ru: "Заголовки колонок в самом файле всегда английские: по ним пишут формулы в Excel, и переименование вслед за языком интерфейса сломало бы уже сохранённые таблицы.",
    en: "Column headers inside the file are always English: formulas in Excel are written against them, and renaming them to follow the interface language would break spreadsheets people have already saved.",
  },

  /* --- детализация: подсказка на числе и заголовки открывающихся списков
   *
   * Заголовок называет, ЧТО за список, а не повторяет подпись плитки: под
   * «Выручкой завершённых» лежат сделки, и человек, открывший панель, обязан
   * увидеть это словами. Дату отбора панель сделок печатает сама (`dealList`,
   * `BASIS_LABEL`), поэтому в заголовках её нет — иначе она стояла бы дважды.
   * У панели лидов такой подписи нет вовсе, и там когорта названа в `hint`.
   */
  /* --- сделки и депозиты */
  drillDealsWithDeposit: {
    ru: "Сделки, за которыми в CRM записан депозит",
    en: "Deals with a deposit recorded in the CRM",
  },
  drillDealsAll: { ru: "Все сделки периода", en: "All deals of the period" },
  drillDealsOpenWithDeposit: {
    ru: "Открытые сделки, за которыми записан депозит",
    en: "Open deals with a deposit recorded",
  },
  drillDealsWon: { ru: "Завершённые праздники", en: "Completed events" },
  drillDealsOpen: { ru: "Сделки в работе", en: "Deals still in the pipeline" },
  drillPaidNoCrmField: {
    ru: "Оплачено через систему бронирования, поле депозита в CRM пусто",
    en: "Paid through the booking system, CRM deposit field empty",
  },
  /* Подписи «плитка — это сумма по этому списку»: под денежной плиткой лежит
     список сделок, и без оговорки его число читалось бы как сама сумма. */
  hintTileIsSumDeposit: {
    ru: "плитка — сумма их депозитов",
    en: "the tile is the sum of their deposits",
  },
  hintTileIsSumAmount: {
    ru: "плитка — сумма их сумм сделок",
    en: "the tile is the sum of their deal amounts",
  },
  /* --- конверсия воронки */
  /* С 12.10.2026 депозит подтверждается оплатой в системе бронирования
     (миграция 097); до 01.09.2026 — первым переходом в Booked. */
  hintSecuredCondition: {
    ru: "депозит подтверждён оплатой, стадия не проигрышная, дубли не считаются",
    en: "the deposit is confirmed by a payment, the stage is not a lost one, duplicates excluded",
  },
  hintClosedCondition: {
    ru: "первая оплата депозита в системе бронирования по сделке (до 01.09.2026 — первый переход в Booked); без оплаты и дубли — в блоке «Депозиты на исправление»",
    en: "the first deposit payment for the deal in the booking system (before 1 Sep 2026 — the first move to Booked); deals without a payment and duplicates are in “Deposits to fix”",
  },
  /* С 04.10.2026 — с исключениями сверки: выигранный дубль не праздник, а
     праздник при открытой сделке — праздник (миграция 090). */
  hintWonStage: {
    ru: "праздник проведён: выиграна или прошёл по чеку Syrve, с исключениями сверки",
    en: "the party took place: won, or held per the Syrve check; reconciliation exceptions applied",
  },
  /* Когорта названа в подсказке у КАЖДОГО списка лидов этого экрана: панель
     лидов, в отличие от панели сделок, дату отбора сама не печатает, а когорта
     здесь другая, чем на Обзоре. */
  hintCohortEntered: {
    ru: "когорта: вошли на стадию менеджера ОП внутри периода, включая созданных раньше",
    en: "cohort: entered a salesperson stage inside the period, including leads created earlier",
  },
  drillCohortWithDeal: {
    ru: "Лиды когорты, по которым появилась сделка",
    en: "Cohort leads that produced a deal",
  },
  /* Отношение двух совокупностей: детализируется только числитель, и это
     сказано словами, а не подразумевается. */
  hintNumeratorOnly: {
    ru: "только числитель конверсии, знаменатель — вся когорта",
    en: "the numerator only; the denominator is the whole cohort",
  },
  drillMedianLeadToManager: {
    ru: "Лиды, по которым посчитана медиана «лид → менеджер»",
    en: "Leads the lead → manager median is computed from",
  },
  drillMedianManagerToDeal: {
    ru: "Лиды, по которым посчитана медиана «менеджер → сделка создана»",
    en: "Leads the manager → deal median is computed from",
  },
  hintMedianIsMiddle: {
    ru: "значений; медиана — середина этого множества",
    en: "values; the median is the middle of this set",
  },
  hintNoSortByValue: {
    ru: "по самой этой величине список отсортировать нечем",
    en: "there is nothing to sort the list by that value with",
  },
  /* --- бренды */
  hintLeadsCreated: {
    ru: "лиды, созданные в периоде",
    en: "leads created in the period",
  },
  drillQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  drillBanquetWon: { ru: "Завершённые банкетные праздники", en: "Completed banquet events" },
  drillBanquetAll: { ru: "Банкетные сделки", en: "Banquet deals" },
  drillLeadsNoPark: { ru: "Лиды без парка", en: "Leads with no park" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;


/**
 * Заголовок и примечание выгрузки приезжают с сервера ключом, а `dbLabel` ждёт
 * словарь строк. Поэтому общий `DATASET` раскладывается на два взгляда: незнакомый
 * ключ (новый набор, которого ещё нет в словаре) покажет английскую подпись
 * сервера, а не пустоту.
 */
const DATASET_TITLE: Record<string, L> = Object.fromEntries(
  Object.entries(DATASET).map(([key, pair]) => [key, pair.title]),
);

const DATASET_NOTE: Record<string, L> = Object.fromEntries(
  Object.entries(DATASET).map(([key, pair]) => [key, pair.note]),
);

const aed = (m: string | number | null | undefined): string =>
  money(m, "AED", { withCode: false });

const aedShort = (m: string | number | null | undefined): string =>
  money(m, "AED", { compact: true, withCode: false });

// ------------------------------------------------------------ мультибренд

/**
 * Какой поток выручки показывать: оба, только банкеты, только билеты.
 *
 * Это переключатель ВИДА, а не фильтр данных. Скрытая колонка не меняет ни
 * одного числа в оставшихся — иначе одно и то же слово значило бы на одном
 * экране разные суммы в зависимости от положения переключателя.
 */
type Stream = "all" | "banquet" | "tickets";

function StreamPicker({
  value,
  onPick,
}: {
  value: Stream;
  onPick: (s: Stream) => void;
}): React.ReactElement {
  const options: Array<[Stream, string]> = [
    ["all", t(TX.streamAll)],
    ["banquet", t(TX.streamBanquet)],
    ["tickets", t(TX.streamTickets)],
  ];
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          className="chip-btn"
          aria-pressed={value === key}
          onClick={() => onPick(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function BrandsScreen({
  report,
  onPickPark,
}: {
  report: BrandReport;
  /** Клик по парку внутри бренда сужает фильтры дашборда до этого парка. */
  onPickPark: (parkKey: string) => void;
}): React.ReactElement {
  const [openBrand, setOpenBrand] = useState<string | null>(null);
  /* Два потока выручки на одном экране заказчик читать не смог: «we're comparing
     banquets and tickets and all the numbers are in the same place». */
  const [stream, setStream] = useState<Stream>("all");
  const showBanquet = stream !== "tickets";
  const showTickets = stream !== "banquet";
  /*
    Два разных состояния, которые раньше были слиты в одно. Парк без прав в
    Syrve не отчитывается никогда; парк с правами может не иметь чеков именно
    за выбранный период. Раньше проверялось только первое, и парк, отчитавшийся
    один раз в 2023-м, вечно числился reporting.
  */
  const everReported = new Set(report.coverage.map((c) => c.park_key));
  const withPos = new Set(
    report.coverage.filter((c) => c.in_period).map((c) => c.park_key),
  );

  /*
    Три итога одного и того же периода, все три считает сервер по трём полям.
    `brandNoPark` не приходит из API и не должен: он выводится, потому что
    вложенность структурная (park_key ≠ NULL влечёт brand_key ≠ NULL), а не
    случайная, и лишнее поле в ответе однажды разошлось бы с этими двумя.
  */
  const u = report.unattributed;
  const inCards = u.leads_total - u.leads_no_brand;
  const inTable = u.leads_total - u.leads_no_park;
  const brandNoPark = u.leads_no_park - u.leads_no_brand;

  const allParks = useSortable(report.parks, {
    park: { kind: "text", get: (p) => p.park_name },
    /* Ячейка печатает ключ бренда рядом с точкой цвета — по нему и порядок. */
    brand: { kind: "text", get: (p) => p.brand_key },
    leads: { kind: "number", get: (p) => p.leads },
    qualified: { kind: "number", get: (p) => p.qualified },
    won: { kind: "number", get: (p) => p.deals_won },
    banquet: { kind: "number", get: (p) => p.banquet_crm_aed_minor },
    avg: { kind: "number", get: (p) => p.avg_party_aed_minor },
    /* Обе колонки кассы у парка без прав в Syrve печатают «нет доступа» и «—»:
       это не ноль продаж, и вставать между настоящими нулями им нельзя. */
    pos: { kind: "number", get: (p) => (withPos.has(p.park_key) ? p.pos_aed_minor : null) },
    online: { kind: "number", get: (p) => p.online_aed_minor },
    tickets: { kind: "number", get: (p) => (withPos.has(p.park_key) ? p.tickets_sold : null) },
  });

  return (
    <>
      {/*
        Строка сверки. Одно и то же число печаталось тремя способами: 9 157 на
        Overview, 7 840 суммой карточек, 7 055 итогом таблицы парков — потому что
        соединение со справочником работает как молчаливый фильтр. Теперь остаток
        виден, и разница читается, а не выясняется запросом.

        Условие показа было `leads_no_brand > 0`, и это пропускало ВТОРОЙ случай.
        Замер по месяцам: в апреле 2025 (61 лид), мае 2025 (30), августе 2025 (5)
        и мае 2026 (75) бренд известен у всех, а парк — нет; примечание молчало,
        а строка таблицы недосчитывала ровно эти лиды. Поэтому теперь примечание
        появляется и когда бренд у всех, и первое предложение тогда опускается:
        говорить «0 лидов не несут бренда» хуже, чем не говорить ничего.
      */}
      {/* Третье слагаемое условия — исключённые покупки билетов: если их
          убрали, а строка сверки не появилась, исключение стало бы
          невидимым, и это ровно тот дефект, от которого страховала вся
          эта строка. */}
      {(u.leads_no_brand > 0 || u.leads_no_park > 0 || u.leads_ticket_purchase > 0) && (
        <div className="note" style={{ marginBottom: 14 }}>
          {u.leads_no_brand > 0 && (
            <>
              <b>
                {t(TX.reconNoBrandLabel)} {num(u.leads_no_brand)}
              </b>{" "}
              {t(TX.reconNoBrandWhy)}{" "}
            </>
          )}
          {t(TX.reconCardsCover)} {num(inCards)} {t(UI.of)} {num(u.leads_total)}{" "}
          {t(TX.reconLeadsInPeriod)}
          {/* Вторая половина нужна только там, где карточка и строка таблицы
              разошлись: при brandNoPark = 0 объяснять нечего, оба покрытия равны. */}
          {brandNoPark > 0 && (
            <>
              {" "}
              {t(TX.reconTableCovers)} {num(inTable)}. {t(TX.reconChannelKnowsBrand)}{" "}
              {num(brandNoPark)}.
              {/* Равенство печатается только когда обе части его непустые: при
                  leads_no_brand = 0 «0 + 75 = 75» не объясняет ничего. */}
              {u.leads_no_brand > 0 && (
                <>
                  {" "}
                  <b>
                    {t(TX.reconNoParkLabel)} {num(u.leads_no_brand)} + {num(brandNoPark)} ={" "}
                    {/* Из трёх чисел равенства детализируется только правое.
                        «Без бренда» требует среза `brandUnknown`, которого нет
                        ни на фронте, ни на сервере, а «бренд знает, парк нет» —
                        двух признаков сразу; формулировки обоих — в отчёте.
                        Открыть под ними похожий список было бы хуже, чем не
                        открывать: 1 333 и 795 ушли бы под одно и то же. */}
                    <DrillNum
                      drill={{
                        title: t(TX.drillLeadsNoPark),
                        hint: t(TX.hintLeadsCreated),
                        /* Тот же признак, что и на сервере в `brands.ts`:
                           покупки билетов экран за лиды не считает, и список
                           под числом обязан считать так же. */
                        slice: ({ parkUnknown: true, ticketPurchase: false }),
                      }}
                    >
                      {num(u.leads_no_park)}
                    </DrillNum>
                  </b>
                  . {t(TX.reconSumIncludes)}
                </>
              )}
            </>
          )}
          {u.leads_ticket_purchase > 0 && (
            <>
              {" "}
              {t(TX.reconTicketsExcluded)}{" "}
              <DrillNum
                drill={{
                  title: t(TX.drillTicketPurchases),
                  hint: t(TX.hintLeadsCreated),
                  slice: { ticketPurchase: true },
                }}
              >
                {num(u.leads_ticket_purchase)}
              </DrillNum>
              .
            </>
          )}
        </div>
      )}

      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        {report.brands.map((b) => {
          const brandParks = report.parks.filter((p) => p.brand_key === b.brand_key);
          const parksWithPos = brandParks.filter((p) => withPos.has(p.park_key)).length;
          const parksNoAccess = brandParks.filter((p) => !everReported.has(p.park_key)).length;
          const open = openBrand === b.brand_key;
          return (
            <div
              key={b.brand_key}
              className="card"
              style={{ borderTop: `3px solid ${BRAND_COLOR[b.brand_key] ?? "var(--accent)"}` }}
            >
              <div className="card-head">
                <h3>{b.brand_name}</h3>
                <span className="hint">
                  {b.parks} {plural(b.parks, TX.parksCount)}
                </span>
                <span className="spacer" />
                <button
                  className="chip-btn"
                  aria-pressed={open}
                  onClick={() => setOpenBrand(open ? null : b.brand_key)}
                >
                  {open ? t(TX.hideParks) : t(UI.parks)}
                </button>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <BrandLine
                  label={t(TX.posRevenue)}
                  value={
                    parksWithPos === 0
                      ? t(TX.noSyrveAccess)
                      : `${aedShort(b.pos_aed_minor)} AED`
                  }
                  foot={
                    parksWithPos === 0
                      ? `${t(TX.tillNotAvailable)}${
                          parksNoAccess > 0
                            ? ` · ${parksNoAccess} ${plural(parksNoAccess, TX.parksNoAccessAtAll)}`
                            : ""
                        }`
                      : `${parksWithPos} ${t(UI.of)} ${b.parks} ${plural(b.parks, TX.parksReporting)}${
                          parksNoAccess > 0 ? ` · ${parksNoAccess} ${t(TX.withNoSyrveAccess)}` : ""
                        } · ${num(Math.round(Number(b.tickets_sold)))} ${t(TX.ticketsSold)}`
                  }
                  muted={parksWithPos === 0}
                />
                {/*
                  Состав кассы прямо на карточке. Без него «982K AED» рядом с
                  «27.7K AED in CRM» читается как загадка: заказчик так и
                  спросил — откуда остальная выручка. Ответ в том, что банкеты
                  это пятая часть, а основное — билеты и еда.
                */}
                {parksWithPos > 0 && (
                  <BrandLine
                    label={t(TX.whatTillTook)}
                    value={`${aedShort(b.pos_tickets_aed_minor)} AED ${t(TX.onTickets)}`}
                    foot={`${aedShort(b.pos_food_aed_minor)} AED ${t(TX.onFood)} · ${aedShort(b.pos_banquet_aed_minor)} AED ${t(TX.onBanquet)} · ${t(TX.partyChecksOverlap)}`}
                  />
                )}
                <BrandLine
                  label={t(UI.conversations)}
                  value={
                    <DrillNum
                      drill={{
                        title: `${b.brand_name} — ${t(UI.conversations)}`,
                        hint: t(TX.hintLeadsCreated),
                        slice: ({ brandKey: b.brand_key }),
                      }}
                    >
                      {num(b.leads)}
                    </DrillNum>
                  }
                  foot={
                    <>
                      <DrillNum
                        drill={{
                          title: `${b.brand_name} — ${t(TX.drillQualified)}`,
                          hint: t(TX.hintLeadsCreated),
                          slice: ({ brandKey: b.brand_key, qualified: true }),
                        }}
                      >
                        {num(b.qualified)}
                      </DrillNum>{" "}
                      {t(TX.qualifiedLower)} ({pct(b.qualified, b.leads)})
                    </>
                  }
                />
                {/*
                  Две суммы банкетов рядом, и складывать их нельзя. Касса берёт
                  деньги, пришедшие в терминал за период; CRM с 24.09.2026 берёт
                  праздники, ПРОШЕДШИЕ в периоде (api/completed.ts). До того CRM
                  брала сделки, СОЗДАННЫЕ в периоде и
                  уже выигранные. Замер по Leo & Loona за 01-08.09.2026: касса
                  90 014 AED против 27 660 AED в CRM — втрое больше.
                */}
                <BrandLine
                  label={t(TX.banquetDeals)}
                  value={
                    <>
                      <DrillNum
                        drill={{
                          kind: "deals",
                          title: `${b.brand_name} — ${t(TX.drillBanquetWon)}`,
                          slice: { dateBasis: "party", brandKey: b.brand_key, outcome: "held" },
                        }}
                      >
                        {num(b.deals_won)}
                      </DrillNum>{" "}
                      {t(TX.wonLower)}
                    </>
                  }
                  foot={
                    <>
                      <DrillNum
                        drill={{
                          kind: "deals",
                          title: `${b.brand_name} — ${t(TX.drillBanquetAll)}`,
                          slice: { dateBasis: "create", brandKey: b.brand_key },
                        }}
                      >
                        {num(b.deals)}
                      </DrillNum>{" "}
                      {t(TX.createdInPeriod)} ·{" "}
                      {/* Сумма в CRM — та же совокупность, что и «выиграно»:
                          складываются суммы выигранных сделок. Поэтому список
                          под ней тот же, и заголовок называет сделки, а не
                          деньги. */}
                      <DrillNum
                        drill={{
                          kind: "deals",
                          title: `${b.brand_name} — ${t(TX.drillBanquetWon)}`,
                          hint: t(TX.hintTileIsSumAmount),
                          slice: { dateBasis: "party", brandKey: b.brand_key, outcome: "held" },
                        }}
                      >
                        {aedShort(b.banquet_crm_aed_minor)}
                      </DrillNum>{" "}
                      AED {t(TX.inCrm)}
                      {/* Касса под детализацию не идёт: у чека Syrve нет
                          идентификатора в источнике — отчёт приходит
                          агрегатом по дате, отделу, категории и блюду. */}
                      {parksWithPos > 0
                        ? `${t(TX.against)} ${aedShort(b.pos_banquet_aed_minor)} ${t(TX.ofBanquetMoneyAtTill)}`
                        : ""}
                      {/* Средний чек на праздник: просьба со сдачи, «spend
                          average, средний чек — нам это важно тоже видеть
                          здесь». Считается по выигранным сделкам с суммой
                          больше дирхама: заглушки с нулём тянут среднее вниз. */}
                      {b.avg_party_aed_minor !== null
                        ? ` · ${aedShort(b.avg_party_aed_minor)} ${t(TX.avgPerPartyLower)}`
                        : ""}
                    </>
                  }
                />
                {showTickets && (
                  <BrandLine
                    label={t(TX.onlineTickets)}
                    value={`${aedShort(b.online_aed_minor)} AED`}
                    foot={`${num(b.online_bookings)} ${t(TX.paidBookingsOnSite)}`}
                  />
                )}
              </div>

              {open && (
                <BrandParksTable parks={brandParks} withPos={withPos} onPickPark={onPickPark} />
              )}
            </div>
          );
        })}
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        {/* ДВА графика про поток, а не один. Слева весь входящий объём, справа
            та его часть, за которую отвечает отдел продаж. Заказчик просил
            именно пару: «такой же график, только именно с квалифицированными
            лидами по дням рождения», — потому что общий счёт на девяносто семь
            процентов состоит из касаний не по теме и о работе продаж молчит. */}
        <Card title={t(TX.leadsByMonth)} hint={t(TX.perBrand)}>
          <BrandTrend monthly={report.monthly} metric="leads" />
        </Card>
        <Card title={t(TX.qualifiedByMonth)} hint={t(TX.qualifiedLeadsHint)}>
          <BrandTrend monthly={report.monthly} metric="qualified" />
        </Card>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Card title={t(TX.posRevenueByMonth)} hint={t(TX.aedPerBrand)}>
          <BrandTrend monthly={report.monthly} metric="pos" />
        </Card>
      </div>

      <Card
        title={t(TX.allParksSideBySide)}
        hint={stream === "all" ? undefined : t(TX.streamHint)}
        right={<StreamPicker value={stream} onPick={setStream} />}
      >
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={allParks} col="park">{t(UI.park)}</Th>
                <Th sort={allParks} col="brand">{t(UI.brand)}</Th>
                <Th sort={allParks} col="leads" num>{t(UI.conversations)}</Th>
                <Th sort={allParks} col="qualified" num>{t(TX.colQualified)}</Th>
                {showBanquet && (
                  <>
                    <Th sort={allParks} col="won" num>{t(TX.colDealsWon)}</Th>
                    <Th sort={allParks} col="banquet" num>{t(TX.colBanquetsCrm)}</Th>
                    <Th sort={allParks} col="avg" num>{t(TX.colAvgParty)}</Th>
                  </>
                )}
                <Th sort={allParks} col="pos" num>{t(TX.colPosRevenueAed)}</Th>
                {showTickets && (
                  <>
                    <Th sort={allParks} col="online" num>{t(TX.colOnlineTicketsAed)}</Th>
                    <Th sort={allParks} col="tickets" num>{t(TX.colTicketsSold)}</Th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {allParks.rows.map((p) => {
                const noPos = !withPos.has(p.park_key);
                return (
                  <tr key={p.park_key}>
                    <td>{p.park_name}</td>
                    <td>
                      <span
                        className="dot"
                        style={{ background: BRAND_COLOR[p.brand_key] ?? "var(--accent)" }}
                      />{" "}
                      {p.brand_key}
                    </td>
                    <td className="num">
                      <DrillNum
                        enabled={p.leads > 0}
                        drill={{
                          title: `${p.park_name} — ${t(UI.conversations)}`,
                          hint: t(TX.hintLeadsCreated),
                          slice: ({ parkKey: p.park_key }),
                        }}
                      >
                        {num(p.leads)}
                      </DrillNum>
                    </td>
                    <td className="num">
                      <DrillNum
                        enabled={p.qualified > 0}
                        drill={{
                          title: `${p.park_name} — ${t(TX.drillQualified)}`,
                          hint: t(TX.hintLeadsCreated),
                          slice: ({ parkKey: p.park_key, qualified: true }),
                        }}
                      >
                        {num(p.qualified)}
                      </DrillNum>
                    </td>
                    {showBanquet && (
                      <>
                        <td className="num">
                          <DrillNum
                            enabled={p.deals_won > 0}
                            drill={{
                              kind: "deals",
                              title: `${p.park_name} — ${t(TX.drillBanquetWon)}`,
                              slice: { dateBasis: "party", parkKey: p.park_key, outcome: "held" },
                            }}
                          >
                            {num(p.deals_won)}
                          </DrillNum>
                        </td>
                        <td className="num">
                          <DrillNum
                            enabled={Number(p.banquet_crm_aed_minor) > 0}
                            drill={{
                              kind: "deals",
                              title: `${p.park_name} — ${t(TX.drillBanquetWon)}`,
                              hint: t(TX.hintTileIsSumAmount),
                              slice: { dateBasis: "party", parkKey: p.park_key, outcome: "held" },
                            }}
                          >
                            {aed(p.banquet_crm_aed_minor)}
                          </DrillNum>
                        </td>
                        {/* Средний чек детализации не имеет: за ним стоит частное
                            двух чисел, а не совокупность записей. */}
                        <td className="num">
                          {p.avg_party_aed_minor === null ? "—" : aed(p.avg_party_aed_minor)}
                        </td>
                      </>
                    )}
                    {/* Касса, онлайн-билеты и билеты детализации не имеют, и это
                        не недоделка. Касса приходит из Syrve агрегатом (чек без
                        идентификатора), онлайн-билеты — суточной строкой
                        `mart.online_ticket_daily` по парку: строки продажи в
                        хранилище нет вовсе, значит нет и лида за ней. */}
                    <td className="num" style={noPos ? { color: "var(--ink-3)" } : undefined}>
                      {noPos ? t(TX.noAccess) : aed(p.pos_aed_minor)}
                    </td>
                    {showTickets && (
                      <>
                        <td className="num">{aed(p.online_aed_minor)}</td>
                        <td className="num">
                          {noPos ? "—" : num(Math.round(Number(p.tickets_sold)))}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          {t(TX.allParksNote)}
        </div>
      </Card>
    </>
  );
}

/**
 * Парки внутри карточки бренда. Отдельный компонент только ради хука
 * сортировки: таблица рисуется внутри `report.brands.map(...)` и по условию
 * `open`, а хук в цикле и под условием — нарушение правила хуков. Разметка
 * ячеек и порядок строк по умолчанию (серверный `ORDER BY dp.sort`) те же.
 */
function BrandParksTable({
  parks,
  withPos,
  onPickPark,
}: {
  parks: BrandReport["parks"];
  /** Парки, по которым касса за выбранный период есть. */
  withPos: Set<string>;
  onPickPark: (parkKey: string) => void;
}): React.ReactElement {
  const S = useSortable(parks, {
    park: { kind: "text", get: (p) => p.park_name },
    leads: { kind: "number", get: (p) => p.leads },
    completed: { kind: "number", get: (p) => p.deals_won },
    avg: { kind: "number", get: (p) => p.avg_party_aed_minor },
    /* «нет доступа» — не ноль кассы: без прав в Syrve сортировать нечем. */
    pos: { kind: "number", get: (p) => (withPos.has(p.park_key) ? p.pos_aed_minor : null) },
  });

  return (
    <div className="scroll-x" style={{ marginTop: 12 }}>
      <table className="data">
        <thead>
          <tr>
            <Th sort={S} col="park">{t(UI.park)}</Th>
            <Th sort={S} col="leads" num>{t(UI.conversations)}</Th>
            <Th sort={S} col="completed" num>{t(TX.colCompleted)}</Th>
            <Th sort={S} col="avg" num>{t(TX.colAvgParty)}</Th>
            <Th sort={S} col="pos" num>{t(TX.colPosAed)}</Th>
          </tr>
        </thead>
        <tbody>
          {S.rows.map((p) => (
            <tr
              key={p.park_key}
              style={{ cursor: "pointer" }}
              title={t(TX.filterToThisPark)}
              onClick={() => onPickPark(p.park_key)}
            >
              <td>
                {p.park_name}
                <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {p.city} · {p.currency}
                </div>
              </td>
              {/* Щелчок по строке сужает фильтры дашборда до парка, поэтому
                  ссылка внутри ячейки обязана останавливать всплытие — иначе
                  одно нажатие делало бы два дела. Это делает `DrillNum`. */}
              <td className="num">
                <DrillNum
                  enabled={p.leads > 0}
                  drill={{
                    title: `${p.park_name} — ${t(UI.conversations)}`,
                    hint: t(TX.hintLeadsCreated),
                    slice: ({ parkKey: p.park_key }),
                  }}
                >
                  {num(p.leads)}
                </DrillNum>
              </td>
              <td className="num">
                <DrillNum
                  enabled={p.deals_won > 0}
                  drill={{
                    kind: "deals",
                    title: `${p.park_name} — ${t(TX.drillBanquetWon)}`,
                    slice: { dateBasis: "party", parkKey: p.park_key, outcome: "held" },
                  }}
                >
                  {num(p.deals_won)}
                </DrillNum>
              </td>
              {/* Средний чек — частное двух чисел, детализации за ним нет. */}
              <td className="num">
                {p.avg_party_aed_minor === null ? "—" : aed(p.avg_party_aed_minor)}
              </td>
              <td className="num">
                {withPos.has(p.park_key) ? aed(p.pos_aed_minor) : t(TX.noAccess)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BrandLine({
  label,
  value,
  foot,
  muted,
}: {
  label: string;
  /* Не `string`: в число и в подпись встают ссылки детализации. Печатается тот
     же текст — узел вместо строки разметку не меняет. */
  value: React.ReactNode;
  foot: React.ReactNode;
  muted?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div className="metric-label caps">{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: muted ? "var(--ink-3)" : undefined }}>
        {value}
      </div>
      <div className="metric-foot">{foot}</div>
    </div>
  );
}

/**
 * Помесячный тренд по брендам.
 *
 * Месяц, а не день: три бренда на дневном шаге — это частокол, из которого не
 * читается ничего. Отчёт называется операционным, но смотрят в него ради
 * направления, а не ради конкретного вторника.
 */
function BrandTrend({
  monthly,
  metric,
}: {
  monthly: BrandReport["monthly"];
  metric: "leads" | "qualified" | "pos";
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const tick = useThemeTick();

  useEffect(() => {
    if (!ref.current) return;
    /* Цвета читаются ВНУТРИ эффекта: они приходят из переменных темы, и на
       светлой странице значения другие. Снаружи они застыли бы на тёмных. */
    const t = chartTheme();
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    const months = [...new Set(monthly.map((m) => m.month))];
    const brands = [...new Set(monthly.map((m) => m.brand_key))];

    chart.setOption({
      grid: { left: 48, right: 12, top: 28, bottom: 24 },
      tooltip: {
        trigger: "axis",
        ...t.tooltip,
        valueFormatter: (v: number) => (metric === "pos" ? `${compact(v)} AED` : num(v)),
      },
      legend: {
        top: 0,
        textStyle: { color: t.ink2, fontFamily: t.font },
        itemHeight: 8,
        itemWidth: 12,
      },
      xAxis: {
        type: "category",
        data: months.map((m) => m.slice(0, 7)),
        ...t.axis,
      },
      yAxis: {
        type: "value",
        splitLine: t.splitLine,
        axisLabel: {
          color: t.ink3,
          fontSize: 11,
          fontFamily: t.font,
          formatter: (v: number) => compact(v),
        },
      },
      series: brands.map((b) => ({
        name: monthly.find((m) => m.brand_key === b)?.brand_name ?? b,
        type: "line",
        smooth: true,
        symbolSize: 6,
        lineStyle: { width: 2 },
        itemStyle: { color: BRAND_COLOR[b] ?? t.series[0] },
        data: months.map((m) => {
          const row = monthly.find((x) => x.month === m && x.brand_key === b);
          if (!row) return 0;
          if (metric === "leads") return row.leads;
          if (metric === "qualified") return row.qualified;
          return Number(row.pos_aed_minor) / 100;
        }),
      })),
    });

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [monthly, metric, tick]);

  return <div ref={ref} style={{ height: 240 }} />;
}

// ------------------------------------------------------------- пайплайн

export function PipelineScreen({
  report,
  delta,
}: {
  report: PipelineReport;
  /** Сравнение с прошлым периодом. Нет — экран работает без значков. */
  delta?: DeltaFn;
}): React.ReactElement {
  const k = report.kpi;
  const hours = (h: string | null): string => {
    if (h === null) return "—";
    const n = Number(h);
    if (n < 1) return `${Math.round(n * 60)} ${t(TX.unitMin)}`;
    // dec(), а не toFixed(): точку в дробной части ставит форматтер, потому
    // что в русском там запятая.
    if (n < 48) return `${dec(n)} ${t(TX.unitHours)}`;
    return `${dec(n / 24)} ${t(TX.unitDays)}`;
  };

  return (
    <>
      {/*
        * Итог периода. Считается по датам событий, а не по когорте лидов: это
        * то, чем руководитель отдела продаж мерит месяц.
        */}
      <div className="grid cols-4" style={{ marginBottom: 14 }}>
        {/*
          Четыре плитки — СДЕЛКИ, и стоят они на трёх разных датах: праздник,
          первый переход в Booked, закрытие. Дату отбора панель печатает сама,
          поэтому три одинаковых заголовка тут невозможны. Замер 10.09.2026 на
          01.07-10.09 (и повторно на трёх других периодах): 231 / 404 / 230 и
          суммы — плитка и список совпадают до единицы.
        */}
        <Kpi
          label={t(TX.securedDeposits)}
          value={num(k.secured_deposits)}
          delta={delta?.("kpi.secured_deposits")}
          foot={`${t(TX.byEventDate)} · ${aedShort(k.secured_deposit_aed_minor)} ${t(TX.aedTakenOutOf)} ${aedShort(k.secured_value_aed_minor)} ${t(TX.ofBanquetValue)}`}
          drill={{
            kind: "deals",
            title: t(TX.securedDeposits),
            hint: t(TX.hintSecuredCondition),
            /* Подтверждённый депозит (миграция 097) уже значит «депозит не
               нулевой»: отдельный hasDeposit проверял бы поле CRM, а у сделок,
               созданных системой бронирования, оно пустое. */
            slice: { dateBasis: "event", outcome: "not_lost", depositConfirmed: true },
          }}
        />
        <Kpi
          label={t(TX.closedDeposits)}
          value={num(k.closed_deposits)}
          delta={delta?.("kpi.closed_deposits")}
          foot={`${t(TX.bySaleDate)} · ${aedShort(k.closed_deposits_aed_minor)} ${t(TX.aedTaken)}`}
          /*
           * Сначала МЕСЯЦЫ праздника, и только потом список.
           *
           * Число собрано по дате, когда взяли деньги, а праздники по этим
           * деньгам расходятся на полгода вперёд. Заказчику под ним нужен
           * ответ «сколько из них сентябрь», а не сто пятьдесят строк подряд:
           * «мне вот эта простыня нужна только для сентября».
           */
          drill={{
            kind: "deals",
            title: t(TX.closedDeposits),
            hint: t(TX.hintClosedCondition),
            byEventMonth: true,
            /* Момент депозита по правилу миграции 097 — тем же выражением,
               что и плитка (`core.v_deal_deposit.deposit_at`). */
            slice: { dateBasis: "deposit" },
          }}
        />
        <Kpi
          label={t(TX.completedEvents)}
          value={num(k.completed_events)}
          delta={delta?.("kpi.completed_events")}
          foot={
            <>
              {t(TX.footCompletedEvents)}
              {Number(k.exceptions_affecting ?? 0) > 0 && (
                <>
                  {" · "}
                  <DrillNum
                    drill={{
                      kind: "exceptions",
                      title: t(TX.drillExceptions),
                      hint: t(TX.hintExceptions),
                      status: "affecting",
                    }}
                  >
                    {num(Number(k.exceptions_affecting))}
                  </DrillNum>{" "}
                  {plural(Number(k.exceptions_affecting), TX.exceptionsApplied)}
                </>
              )}
            </>
          }
          drill={{
            kind: "deals",
            title: t(TX.completedEvents),
            hint: t(TX.hintWonStage),
            slice: { dateBasis: "party", outcome: "held" },
          }}
        />
        <Kpi
          label={t(TX.completedRevenue)}
          value={aedShort(k.completed_revenue_aed_minor)}
          unit="AED"
          delta={delta?.("kpi.completed_revenue_aed_minor")}
          foot={t(TX.footCompletedRevenue)}
          /* Тот же список, что у плитки слева: деньги — сумма по этим же
             сделкам, и подсказка это говорит, чтобы число списка не читалось
             как сама сумма. */
          drill={{
            kind: "deals",
            title: t(TX.completedEvents),
            hint: t(TX.hintTileIsSumAmount),
            slice: { dateBasis: "party", outcome: "held" },
          }}
        />
      </div>

      <div className="note" style={{ marginBottom: 14 }}>
        <b>{t(TX.depositsConfuseTitle)}</b> <b>{t(TX.closedWord)}</b> {t(TX.closedIsWhat)}{" "}
        <b>{t(TX.securedWord)}</b> {t(TX.securedIsWhat1)} <i>{t(TX.thisWord)}</i>{" "}
        {t(TX.securedIsWhat2)} {t(TX.depositsMeasured)}
      </div>

      {/* Что не сошлось между сделкой и оплатой — списком, который клиент
          исправляет сам (решение 12.10.2026, миграция 097). */}
      <div style={{ marginBottom: 14 }}>
        <DepositReviewBlock rows={report.depositReview} />
      </div>

      {/* Когорта: что стало с лидами, вошедшими в работу в этом периоде. */}
      <div className="grid cols-4" style={{ marginBottom: 14 }}>
        {/*
          ОБВЯЗАНА ТОЛЬКО ПЕРВАЯ ПЛИТКА, и остальные три — не забытые.
          Замер 10.09.2026: под когортой лежит `cohort: 'entered_handoff'`,
          список даёт 1 292 при 1 292 на плитке, и так же на трёх других
          периодах.

          Число 264 в подписи («дошли до стадии Park Tour») НЕ обвязано, хотя
          похожий срез есть. Плитка считает лидов, у которых Park Tour попал в
          переходы ВНУТРИ ПЕРИОДА, а `reachedStageId` спрашивает
          `core.v_lead_reached` — «доходил когда-либо». На периоде 01.07-10.09
          оба дают 264, а на июле — 77 против 91: список показал бы под числом
          77 девяносто одну строку. Точная формулировка нужного среза — в
          отчёте.

          «Сделка из Interested» и «Сделка из Park Tour» — та же когорта плюс
          признак `reached_park_tour` (и его отрицание), которого в срезе нет
          вовсе; `NOT reached_park_tour` не выражается и через два вызова.
        */}
        <Kpi
          label={t(TX.enteredStages)}
          value={num(k.reached_handoff)}
          delta={delta?.("kpi.reached_handoff")}
          foot={`${t(TX.enteredInterestedOrTour)} · ${num(k.reached_park_tour)} ${t(TX.gotAParkTour)}`}
          drill={{
            title: t(TX.enteredStages),
            hint: t(TX.hintCohortEntered),
            slice: ({ cohort: "entered_handoff" }),
          }}
        />
        <Kpi
          label={t(TX.dealFromInterested)}
          value={num(k.deal_from_interested)}
          delta={delta?.("kpi.deal_from_interested")}
          foot={`${pct(k.deal_from_interested, k.reached_handoff - k.reached_park_tour)} ${t(TX.ofLeadsNoParkTourStage)}`}
        />
        <Kpi
          label={t(TX.dealFromParkTour)}
          value={num(k.deal_from_park_tour)}
          delta={delta?.("kpi.deal_from_park_tour")}
          foot={`${pct(k.deal_from_park_tour, k.reached_park_tour)} ${t(TX.ofLeadsMovedToParkTour)}`}
        />
        {/*
          * Без inverse: это счётчик сделок, а не нарушений процесса. Примечание
          * ниже прямо оговаривает, что обход воронки не всегда небрежность, и
          * красная стрелка на выросшем числе сделок спорила бы с ним.
          *
          * Без детализации: это ТРЕТЬЯ совокупность — лиды, у которых сделка
          * создана в периоде и НЕТ ни одного перехода на стадию менеджера ОП.
          * Период тут приложен к дате создания СДЕЛКИ, а когорты `created` и
          * `entered_handoff` прикладывают его к лиду. Формулировка нужного
          * среза — в отчёте.
          */}
        <Kpi
          label={t(TX.dealStraightFromInquiry)}
          value={num(k.deal_direct)}
          delta={delta?.("kpi.deal_direct")}
          foot={`${t(TX.skippedFunnelEntirely)} · ${num(k.won_direct)} ${t(TX.alreadyCompleted)}`}
        />
      </div>

      <div className="note" style={{ marginBottom: 14 }}>
        <b>{t(TX.parkTourStageBold)}</b> {t(TX.parkTourNote)}
      </div>

      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        {/* Обе скорости — время ожидания: рост означает, что стали отвечать медленнее. */}
        {/*
          Под медианой открывается МНОЖЕСТВО, по которому она посчитана, и это
          сказано в подсказке числом: «1 292 значения, медиана — середина этого
          множества». Показать «одну медианную сделку» было бы выдумкой.
          Отсортировать список по самой этой величине нечем: у `/api/leads`
          своего параметра сортировки нет, а часы прохождения ступеней в строке
          списка не приходят вовсе — поэтому подсказка это признаёт, а не
          умалчивает.

          Знаменатель второй медианы — те же лиды когорты, у которых появилась
          сделка: замер 10.09.2026 — 186 и на плитке `with_deal`, и в списке.
        */}
        {/*
          * Время первого ответа — просьба операционного директора со сдачи.
          * Стоит рядом с «до передачи менеджеру» нарочно: это две разные
          * скорости, и путать их нельзя. Первая — насколько быстро клиенту
          * вообще ответили, вторая — сколько лид шёл до продавца.
          *
          * Подпись называет отдельно медиану людей: бот отвечает за секунды, и
          * общая медиана с ним внутри говорит о работе отдела продаж ничего.
          */}
        <Kpi
          label={t(TX.firstResponse)}
          value={
            report.response.median_minutes === null
              ? "—"
              : `${num(Math.round(Number(report.response.median_minutes)))} ${t(TX.minutesShort)}`
          }
          foot={
            report.response.n === 0
              ? t(TX.noConversationsInPeriod)
              : `${t(TX.medianOf)} ${num(report.response.n)} · ${t(TX.humansAlone)} ${
                  report.response.median_human_minutes === null
                    ? "—"
                    : `${num(Math.round(Number(report.response.median_human_minutes)))} ${t(TX.minutesShort)}`
                } · ${num(report.response.unanswered)} ${t(TX.withoutAnyReply)}`
          }
        />
        <Kpi
          label={t(TX.leadToManager)}
          value={hours(report.speed.median_hours_to_handoff)}
          delta={delta?.("speed.median_hours_to_handoff", { inverse: true })}
          foot={`${t(TX.medianOf)} ${num(report.speed.n)} · ${t(TX.percentile90)} ${hours(report.speed.p90_hours_to_handoff)}`}
          drill={{
            title: t(TX.drillMedianLeadToManager),
            hint: `${t(TX.hintCohortEntered)} · ${num(report.speed.n)} ${t(TX.hintMedianIsMiddle)} · ${t(TX.hintNoSortByValue)}`,
            slice: ({ cohort: "entered_handoff" }),
          }}
        />
        <Kpi
          label={t(TX.managerToDeal)}
          value={hours(report.speed.median_hours_to_deal)}
          delta={delta?.("speed.median_hours_to_deal", { inverse: true })}
          foot={t(TX.footMedianOnlyWithDeal)}
          drill={{
            title: t(TX.drillMedianManagerToDeal),
            hint: `${t(TX.hintCohortEntered)} · ${num(k.with_deal)} ${t(TX.hintMedianIsMiddle)} · ${t(TX.hintNoSortByValue)}`,
            slice: ({ cohort: "entered_handoff", hasDeal: true }),
          }}
        />
        {/*
          * Без сравнения: показана доля, посчитанная здесь из двух полей, а
          * своего поля в ответе у неё нет. Значок по with_deal встал бы рядом с
          * процентом и читался как изменение процента.
          */}
        {/*
          Отношение: числитель и знаменатель — разные совокупности, пусть и
          вложенные. Детализируется ТОЛЬКО числитель (лиды когорты, у которых
          появилась сделка), и подсказка списка это надписывает: единого списка
          «из чего сложился процент» не существует.
        */}
        <Kpi
          label={t(TX.cohortDealRate)}
          value={pct(k.with_deal, k.reached_handoff)}
          foot={`${num(k.with_deal)} ${t(UI.of)} ${num(k.reached_handoff)} ${t(TX.leadsEnteredStages)}`}
          drill={{
            title: t(TX.drillCohortWithDeal),
            hint: `${t(TX.hintCohortEntered)} · ${t(TX.hintNumeratorOnly)}`,
            slice: ({ cohort: "entered_handoff", hasDeal: true }),
          }}
        />
      </div>

      <div className="note" style={{ marginBottom: 14 }}>
        <b>{t(TX.cohortNoteTitle)}</b> {t(TX.cohortNote1)} <b>{t(TX.enteredBold)}</b>{" "}
        {t(TX.cohortNote2)} <b>{t(TX.createdBold)}</b> {t(TX.cohortNote3)}
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Card title={t(TX.byChannel)} hint={t(TX.whereCohortCameFrom)}>
          <ConversionTable
            rows={report.byChannel.map((r) => ({
              /* Подпись канала берёт словарь по ключу; сентинел «сопоставить
                 не удалось» приезжает вместо ключа и переводится тем же способом. */
              name: dbLabel(CHANNEL, r.channel_key, dbLabel(SENTINEL, r.channel_name, r.channel_name)),
              reached: r.reached_handoff,
              deal: r.with_deal,
              won: r.won_deal,
              conv: r.conv_to_deal_pct,
              revenue: r.revenue_aed_minor,
            }))}
          />
        </Card>
        <Card title={t(TX.byPark)}>
          <ConversionTable
            rows={report.byPark.map((r) => ({
              name: dbLabel(SENTINEL, r.park_name, r.park_name),
              reached: r.reached_handoff,
              deal: r.with_deal,
              won: r.won_deal,
              conv: r.conv_to_deal_pct,
              revenue: r.revenue_aed_minor,
            }))}
          />
        </Card>
      </div>

      <Card title={t(TX.byManager)} hint={t(TX.whoIsWorkingCohort)}>
        {/*
          Единственный из трёх разрезов, где сортировка соврала бы: менеджеры
          приходят обрезанными сервером (`api/operations.ts`, разрез когорты:
          `ORDER BY reached_handoff DESC LIMIT 30`), и порядок по выручке внутри
          первых тридцати читался бы как порядок по всем менеджерам. Признака
          «обрезано» в ответе нет, поэтому заголовки выключены целиком: механизм
          покажет причину подсказкой. Каналы и парки идут без LIMIT и сортируются.
        */}
        <ConversionTable
          partial
          rows={report.byManager.map((r) => ({
            name: dbLabel(SENTINEL, r.manager_name, r.manager_name),
            reached: r.reached_handoff,
            deal: r.with_deal,
            won: r.won_deal,
            conv: r.conv_to_deal_pct,
            revenue: r.revenue_aed_minor,
          }))}
        />
        <div className="note" style={{ marginTop: 12 }}>
          {t(TX.managerNote1)} <b>{t(TX.enteredBold)}</b> {t(TX.managerNote2)}
        </div>
      </Card>

      <div style={{ marginTop: 14 }}>
        <LossReasonsCard data={report.lossReasons} />
      </div>
    </>
  );
}

/**
 * Причины провала лидов: строки — причины, колонки — провальные стадии и итог.
 *
 * Ни одной подписи не зашито: стадии и причины приходят справочниками портала
 * (миграция 157), поэтому правка списка у клиента меняет таблицу сама. Каждое
 * число открывает список лидов тем же срезом, которым оно посчитано: когорта
 * `lost`, стадия — текущая стадия лида, причина — ключ значения.
 */
function LossReasonsCard({ data }: { data: PipelineReport["lossReasons"] }): React.ReactElement {
  const cell = new Map(data.cells.map((c) => [`${c.stage_id}|${c.reason_key}`, c.leads]));
  const byStage = new Map<string, number>();
  const byReason = new Map<string, number>();
  let total = 0;
  for (const c of data.cells) {
    byStage.set(c.stage_id, (byStage.get(c.stage_id) ?? 0) + c.leads);
    byReason.set(c.reason_key, (byReason.get(c.reason_key) ?? 0) + c.leads);
    total += c.leads;
  }
  const reasonName = (r: PipelineReport["lossReasons"]["reasons"][number]): string =>
    r.reason_key === "none" ? t(TX.lossNoReason) : (r.name ?? `${t(TX.lossUnknown)} #${r.reason_key}`);
  /* Число со ссылкой на список. Ноль ссылкой не делаем: пустой список ничего не добавит. */
  const link = (value: number, title: string, slice: { stageId?: string; lossReason?: string }): React.ReactNode =>
    value === 0 ? (
      <span className="muted">0</span>
    ) : (
      <DrillNum drill={{ title, hint: t(TX.lossHint), slice: { cohort: "lost", ...slice } }}>{num(value)}</DrillNum>
    );

  return (
    <Card title={t(TX.lossTitle)} hint={t(TX.lossHint)}>
      {total === 0 ? (
        <div className="empty">{t(TX.lossNone)}</div>
      ) : (
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <th>{t(TX.lossReasonCol)}</th>
                {data.stages.map((s) => (
                  <th key={s.stage_id} className="num" title={s.in_portal ? undefined : t(TX.lossStageGone)}>
                    {s.name}
                  </th>
                ))}
                <th className="num">{t(TX.lossTotal)}</th>
                <th className="num">{t(UI.share)}</th>
              </tr>
            </thead>
            <tbody>
              {data.reasons.map((r) => {
                const name = reasonName(r);
                const rowTotal = byReason.get(r.reason_key) ?? 0;
                return (
                  <tr key={r.reason_key}>
                    <td>
                      {name}
                      {!r.in_portal && r.name !== null && <span className="muted"> · {t(TX.lossRemoved)}</span>}
                    </td>
                    {data.stages.map((s) => (
                      <td key={s.stage_id} className="num">
                        {link(cell.get(`${s.stage_id}|${r.reason_key}`) ?? 0, `${s.name} · ${name}`, {
                          stageId: s.stage_id,
                          lossReason: r.reason_key,
                        })}
                      </td>
                    ))}
                    <td className="num">
                      <b>{link(rowTotal, `${t(TX.lossTitle)} · ${name}`, { lossReason: r.reason_key })}</b>
                    </td>
                    <td className="num muted">{rowTotal > 0 ? pct(rowTotal, total, 1) : "—"}</td>
                  </tr>
                );
              })}
              <tr>
                <td>
                  <b>{t(TX.lossTotal)}</b>
                </td>
                {data.stages.map((s) => (
                  <td key={s.stage_id} className="num">
                    <b>{link(byStage.get(s.stage_id) ?? 0, s.name, { stageId: s.stage_id })}</b>
                  </td>
                ))}
                <td className="num">
                  <b>{link(total, t(TX.lossTitle), {})}</b>
                </td>
                <td className="num muted">{pct(total, total, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="note" style={{ marginTop: 12 }}>
        {t(TX.lossNote)}
      </div>
    </Card>
  );
}

type ConvRow = {
  name: string;
  reached: number;
  deal: number;
  won: number;
  conv: string | null;
  revenue: string;
};

/*
 * Карта колонок на уровне модуля: строки в эту таблицу приходят от трёх
 * разрезов (каналы, парки, менеджеры) уже подготовленными, и одна карта — это
 * гарантия, что все три сортируются одинаково. Подпись уже переведена
 * вызывающей стороной (`dbLabel(CHANNEL, …)`), поэтому текстовая колонка
 * сравнивает именно её, а не ключ базы.
 */
const CONV_COLS: Record<keyof ConvRow, SortColumn<ConvRow>> = {
  name: { kind: "text", get: (r) => r.name },
  reached: { kind: "number", get: (r) => r.reached },
  deal: { kind: "number", get: (r) => r.deal },
  won: { kind: "number", get: (r) => r.won },
  /* Процент считает сервер и присылает null там, где знаменатель ноль, — ячейка
     печатает прочерк, и в конец такую строку уводит сам механизм. */
  conv: { kind: "number", get: (r) => r.conv },
  revenue: { kind: "number", get: (r) => r.revenue },
};

/**
 * ДЕТАЛИЗАЦИИ ЗДЕСЬ НЕТ, И ЭТО РЕШЕНИЕ, А НЕ ПРОПУСК.
 *
 * Колонкам «Дошли», «Сделка», «Завершено» нужен срез когорты плюс ключ строки,
 * и ключа не хватает у всех трёх разрезов, каждому по своей причине.
 *
 *   • Каналы. `channel_key` в ответе есть, но среза по каналу у списка лидов
 *     нет, а ближайший `sourceGroup` — ДРУГОЕ понятие. Замер 10.09.2026:
 *     канал whatsapp — 797 на плитке против 694 в списке, webform — 52 против
 *     16, meta_organic — 20 против 29. То есть срез существует и врёт; обвязать
 *     им значило бы поставить под числом список другой совокупности.
 *   • Парки. В ответе только `park_name` (`pipelineConversion` в
 *     `api/dashboard.ts` ключ не отдаёт), а срез спрашивает `parkKey`.
 *     Сопоставлять имя обратно в ключ на клиенте нельзя: «(not identified)» —
 *     это сентинел, а не парк.
 *   • Менеджеры. В ответе только имя, срез спрашивает `managerId`
 *     (`bitrix_user_id`). Двух менеджеров с одинаковым именем имя не различит.
 *
 * Колонка «Выручка» — сделки, и ей нужен тот же ключ строки, поэтому она стоит
 * ровно там же. Точные формулировки нужного — в отчёте.
 */
function ConversionTable({
  rows,
  partial = false,
}: {
  rows: ConvRow[];
  /** Разрез обрезан на сервере: сортировать видимую часть значило бы соврать. */
  partial?: boolean;
}): React.ReactElement {
  const max = Math.max(...rows.map((r) => r.reached), 1);
  const S = useSortable(rows, CONV_COLS, { partial });
  return (
    <div className="scroll-x">
      <table className="data">
        <thead>
          <tr>
            <Th sort={S} col="name">{t(TX.colName)}</Th>
            <Th sort={S} col="reached" num>{t(TX.colReached)}</Th>
            <Th sort={S} col="deal" num>{t(TX.colDeal)}</Th>
            <Th sort={S} col="won" num>{t(TX.colCompleted)}</Th>
            <Th sort={S} col="conv" num>{t(TX.colConv)}</Th>
            <Th sort={S} col="revenue" num>{t(TX.colRevenueAed)}</Th>
          </tr>
        </thead>
        <tbody>
          {S.rows.map((r) => (
            <tr key={r.name}>
              <td>
                {r.name}
                <div
                  style={{
                    height: 3,
                    marginTop: 4,
                    borderRadius: 2,
                    width: `${(100 * r.reached) / max}%`,
                    background: "var(--accent)",
                    opacity: 0.5,
                  }}
                />
              </td>
              <td className="num">{num(r.reached)}</td>
              <td className="num">{num(r.deal)}</td>
              <td className="num">{num(r.won)}</td>
              {/* Процент приезжает готовым: печатается тем же правилом, что и
                  вычисленный, — знак процента ставит `pctValue`, а не разметка. */}
              <td className="num">{r.conv === null ? "—" : pctValue(r.conv)}</td>
              <td className="num">{aed(r.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------------------------------------------------- сделки и депозиты

export function OperationsScreen({
  report,
  delta,
}: {
  report: OperationsReport;
  /** Сравнение с прошлым периодом. Нет — экран работает без значков. */
  delta?: DeltaFn;
}): React.ReactElement {
  const d = report.deposits;
  const pf = report.paymentFunnel;
  const rec = report.reconcile;
  /* Имя `t` занято функцией перевода — она зовётся во всём файле, а тайминги
     только здесь, поэтому короткое имя уступает именно тут. */
  const tm = report.timing;

  const attention = report.attention.filter((a) => a.deals > 0);
  const attnSort = useSortable(attention, {
    /* Вид проблемы переведён словарём — сортируется напечатанное, не ключ. */
    issue: { kind: "text", get: (a) => dbLabel(ATTENTION, a.issue, a.label) },
    deals: { kind: "number", get: (a) => a.deals },
    amount: { kind: "number", get: (a) => a.amount_aed_minor },
  });


  return (
    <>
      {/* Плиток три, а не четыре: «Держится под открытыми сделками» снята (причина
          у самого места, где она стояла). Ряд пересчитан следом — оставь `cols-4`,
          и четверть ряда пустует, а пустота читается как несработавшая загрузка. */}
      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        {/*
          Две плитки слева считают поле сделки в CRM, карточка «Deposit payments
          confirmed» ниже — систему бронирования. Раньше все три назывались
          словом «депозит», и разница между ними читалась как ошибка расчёта;
          теперь источник стоит в самой подписи, а расчёт не тронут.

          Обе денежные плитки детализируются списком СДЕЛОК, по которым сумма
          сложена; подсказка списка это надписывает, иначе «446 сделок»
          читалось бы как сама сумма. Замер 10.09.2026 на 01.07-10.09, эпоха
          managed: 446 сделок с депозитом и сумма их депозитов 66 489 412
          минорных единиц — ровно то, что на плитке; у «Выручки завершённых»
          230 сделок и 43 582 527.
        */}
        <Kpi
          label={t(UI.depositRecorded)}
          value={aedShort(d["deposit_all_aed_minor"])}
          unit="AED"
          delta={delta?.("deposits.deposit_all_aed_minor")}
          foot={
            <>
              {t(TX.byDealCreationDate)} ·{" "}
              <DrillNum
                drill={{
                  kind: "deals",
                  title: t(TX.drillDealsWithDeposit),
                  slice: { dateBasis: "create", hasDeposit: true },
                }}
              >
                {num(d["with_deposit"])}
              </DrillNum>{" "}
              {t(UI.of)}{" "}
              <DrillNum
                drill={{
                  kind: "deals",
                  title: t(TX.drillDealsAll),
                  slice: { dateBasis: "create" },
                }}
              >
                {num(d["deals"])}
              </DrillNum>{" "}
              {t(TX.dealsCarryDeposit)}
              {report.uncounted.deals > 0 && (
                <>
                  {t(TX.andMore)}{" "}
                  {/* Седьмой вид расхождения: оплата прошла, а поле в CRM
                      пусто. Условие лежит в том же `core.v_deal_issue`, что и
                      шесть строк «Требует внимания» — замер 17 и 17. */}
                  <DrillNum
                    drill={{
                      kind: "deals",
                      title: t(TX.drillPaidNoCrmField),
                      slice: { dateBasis: "create", issue: "deposit_uncounted" },
                    }}
                  >
                    {num(report.uncounted.deals)}
                  </DrillNum>{" "}
                  {t(TX.paidWithoutCrmField)}
                </>
              )}
            </>
          }
          drill={{
            kind: "deals",
            title: t(TX.drillDealsWithDeposit),
            hint: t(TX.hintTileIsSumDeposit),
            slice: { dateBasis: "create", hasDeposit: true },
          }}
        />
        {/*
          ПЛИТКИ «Держится под открытыми сделками» ЗДЕСЬ БОЛЬШЕ НЕТ.

          Заказчик на сдаче: «но они не нужны здесь, это просто сбивает с толку»,
          «remove this 96, it's confusing, it's not needed». Число было ЧАСТЬЮ
          соседнего — те же депозиты минус уже прошедшие праздники, — и рядом с
          ним читалось как ещё одни деньги. Сервер поле по-прежнему считает: оно
          остаётся в ответе и в выгрузке, просто не занимает место на экране.
        */}
        <Kpi
          label={t(TX.completedRevenue)}
          value={aedShort(d["revenue_won_aed_minor"])}
          unit="AED"
          delta={delta?.("deposits.revenue_won_aed_minor")}
          /*
           * Сколько праздников стоит за деньгами — первой же строкой.
           *
           * Заказчик на сдаче: «23.5k completed revenue, but how many exactly
           * events completed?» Сумма без количества не читается: неясно, это
           * пять крупных праздников или полсотни мелких, и средний чек в уме
           * не считается. Просила «маленькой строчкой» — вот она.
           */
          foot={
            <>
              <DrillNum
                drill={{
                  kind: "deals",
                  title: t(TX.drillDealsWon),
                  hint: t(TX.hintTileIsSumAmount),
                  slice: { dateBasis: "party", outcome: "held" },
                }}
              >
                {num(Number(d["deals_won"] ?? 0))}
              </DrillNum>{" "}
              {plural(Number(d["deals_won"] ?? 0), TX.eventsCompletedCounted)}
              {Number(d["deals_won"] ?? 0) > 0 && (
                <>
                  {" · "}
                  {aedShort(
                    Math.round(
                      Number(d["revenue_won_aed_minor"] ?? 0) / Number(d["deals_won"] ?? 1),
                    ),
                  )}{" "}
                  {t(TX.avgPerParty)}
                </>
              )}
              {Number(d["exceptions_affecting"] ?? 0) > 0 && (
                <>
                  {" · "}
                  <DrillNum
                    drill={{
                      kind: "exceptions",
                      title: t(TX.drillExceptions),
                      hint: t(TX.hintExceptions),
                      status: "affecting",
                    }}
                  >
                    {num(Number(d["exceptions_affecting"]))}
                  </DrillNum>{" "}
                  {plural(Number(d["exceptions_affecting"]), TX.exceptionsApplied)}
                </>
              )}
              {" · "}
              <DrillNum
                drill={{
                  kind: "deals",
                  title: t(TX.drillDealsOpen),
                  hint: t(TX.hintTileIsSumAmount),
                  slice: { dateBasis: "create", outcome: "open" },
                }}
              >
                {aedShort(d["pipeline_open_aed_minor"])}
              </DrillNum>{" "}
              {t(TX.stillInPipeline)}
            </>
          }
          drill={{
            kind: "deals",
            title: t(TX.drillDealsWon),
            hint: t(TX.hintTileIsSumAmount),
            slice: { dateBasis: "party", outcome: "held" },
          }}
        />
        {/*
          * Без сравнения: список ближайших событий не фильтруется периодом — за
          * прошлый период пришёл бы тот же самый список, и значок всегда
          * показывал бы «без изменений», как будто это измерено.
          *
          * С детализацией с 19.10.2026 — вместо таблицы на 60 строк, что стояла
          * ниже. Список получает своё окно (`filters`), а не период с верхней
          * панели: окно считает сервер, дубайскими датами, тем же условием,
          * что и число. Эпоха не применяется — это расписание, а не когорта.
          */}
        <Kpi
          label={t(TX.upcomingEvents)}
          value={num(report.upcoming.events)}
          drill={{
            kind: "deals",
            title: `${t(TX.upcomingEvents)} · ${report.upcoming.from_day} — ${report.upcoming.to_day}`,
            hint: t(TX.hintUpcoming),
            slice: { dateBasis: "event", outcome: "not_lost" },
            filters: { from: report.upcoming.from_day, to: report.upcoming.to_day, era: "all" },
          }}
          foot={
            <>
              {t(TX.nextWord)} {report.upcomingDays} {t(TX.daysNotFiltered)}
              {report.upcoming.without_paid_booking > 0 && (
                <>
                  {" · "}
                  <DrillNum
                    drill={{
                      kind: "deals",
                      title: `${t(TX.upcomingEvents)} · ${t(TX.withoutPaidBooking)}`,
                      hint: t(TX.hintUpcoming),
                      slice: { dateBasis: "event", outcome: "not_lost", hasPaidBooking: false },
                      filters: { from: report.upcoming.from_day, to: report.upcoming.to_day, era: "all" },
                    }}
                  >
                    {num(report.upcoming.without_paid_booking)}
                  </DrillNum>{" "}
                  {t(TX.withoutPaidBooking)}
                </>
              )}
            </>
          }
        />
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        {/*
          ВОРОНКА ОПЛАТЫ БЕЗ ДЕТАЛИЗАЦИИ, И ЭТО РЕШЕНИЕ.
          Её ступени — БРОНИ, а не сделки: период приложен к дате создания
          брони, бронь бывает вовсе без сделки, а у сделки бывает несколько
          броней. Списка броней в системе нет — есть список сделок и список
          лидов. Замер 10.09.2026 (15.08-10.09): оплаченных броней 157, а
          сделок с оплаченной бронью 148 — то есть даже ближайший срез
          `hasPaidBooking` показал бы под числом 157 сто сорок восемь строк.

          Медиана «ссылка → оплата» — по платежам того же источника, и она к
          тому же не фильтруется периодом вовсе.

          Числа сверки ниже («на 132 сделках, которые знают обе системы») тоже
          не обвязаны: у стороны броней период приложен к созданию БРОНИ, а у
          среза `hasPaidBooking` периода нет вовсе. На трёх замеренных периодах
          числа совпали (132/132, 23/23, 128/128), но выражения разные, и
          первая же сделка периода с бронью, созданной до периода, разведёт их
          молча. Формулировка нужного среза — в отчёте.
        */}
        <Card title={t(UI.depositConfirmed)} hint={t(TX.fromBookingSystem)}>
          <div className="grid cols-3" style={{ gap: 10 }}>
            <MiniStat label={t(TX.bookings)} value={num(pf.bookings)} />
            <MiniStat label={t(TX.paid)} value={num(pf.paid)} foot={pct(pf.paid, pf.bookings)} />
            {/* Единственная сумма денег в карточке — ей и нужна отметка источника:
                её сравнивают с плиткой CRM выше, и раньше обе звались депозитом. */}
            <MiniStat
              label={t(TX.collected)}
              value={`${aedShort(pf.paid_aed_minor)} AED`}
              foot={t(UI.depositConfirmedShort)}
            />
            <MiniStat label={t(TX.paymentLinkSent)} value={num(pf.link_sent)} />
            <MiniStat label={t(TX.expired)} value={num(pf.expired)} />
            <MiniStat label={t(TX.cancelled)} value={num(pf.cancelled)} />
          </div>
          <div style={{ marginTop: 14 }}>
            <MiniStat
              label={t(TX.linkToPayment)}
              value={tm.median_hours === null ? "—" : `${Number(tm.median_hours) < 1 ? `${Math.round(Number(tm.median_hours) * 60)} ${t(TX.unitMin)}` : `${tm.median_hours} ${t(TX.unitHours)}`}`}
              foot={`${t(TX.medianOf)} ${num(tm.n)} ${t(TX.payments)} · ${num(tm.within_1h)} ${t(TX.withinAnHour)}, ${num(tm.within_24h)} ${t(TX.withinADay)}`}
            />
          </div>
          {/*
            Сверка двух сумм депозитов на одном экране. Без неё «96.8K здесь и
            91.4K тут» читается как ошибка одного из чисел, хотя это два разных
            отбора: CRM берёт сделки, созданные в периоде, а система бронирования
            — брони, созданные в периоде и оплаченные.
          */}
          <div className="note" style={{ marginTop: 12 }}>
            <b>
              {t(TX.whyDiffersFrom)}
              {t(UI.depositRecorded)}
              {t(TX.quoteClose)}
            </b>{" "}
            {t(TX.neitherIsWrong)} {t(UI.depositTwoFacts)}
            <br />
            CRM {aedShort(d["deposit_all_aed_minor"])} AED ={" "}
            {aedShort(rec.crm_both_aed_minor)} {t(TX.onThe)} {num(rec.both_deals)}{" "}
            {t(TX.dealsBothSystemsKnow)} + {aedShort(rec.crm_only_aed_minor)} {t(TX.on)}{" "}
            {num(rec.crm_only_deals)} {t(TX.dealsNoPaidBooking)}.
            <br />
            {t(TX.bookingSystem)} {aedShort(pf.paid_aed_minor)} AED ={" "}
            {aedShort(rec.booking_both_aed_minor)} {t(TX.onTheSame)} {num(rec.both_deals)}{" "}
            {t(TX.dealsWord)} + {aedShort(rec.booking_only_aed_minor)} {t(TX.on)}{" "}
            {num(rec.booking_only)} {t(TX.paymentsWhoseDeal)}.
            <br />
            {t(TX.bothSystemsNotEqual)}
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.bitrixPrepaymentFlag)}
            {report.uncounted.deals > 0 && (
              <>
                {" "}
                <b>
                  {num(report.uncounted.deals)} {t(TX.dealsTook)}{" "}
                  {aedShort(report.uncounted.aed_minor)} {t(TX.aedThroughBookingSystem)}
                </b>{" "}
                {t(TX.moneyMissingFrom)}
                {t(UI.depositRecorded)}
                {t(TX.moneyMissingFix)}
              </>
            )}
            {pf.paid > pf.link_sent && (
              <>
                {" "}
                {t(TX.morePaidThanLinks)} {num(tm.n)} {t(UI.of)} {num(pf.paid)} {t(TX.payments)}.
              </>
            )}
          </div>
        </Card>

        <Card title={t(TX.needsAttention)} hint={t(TX.eachRowFixable)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={attnSort} col="issue">{t(TX.colIssue)}</Th>
                  <Th sort={attnSort} col="deals" num>{t(UI.deals)}</Th>
                  <Th sort={attnSort} col="amount" num>{t(TX.colAmountAed)}</Th>
                </tr>
              </thead>
              <tbody>
                {/*
                  БУКВАЛЬНЫЙ ПРИМЕР ЗАКАЗЧИКА: «видишь метрику „Выиграна, но
                  депозит не записан“ — 2, можешь нажать на неё, и откроется
                  список из этих сделок». Заголовок списка — та же подпись, что
                  и в строке, потому что открывают её и ждут именно её.

                  Условие среза (`issue`) и условие плитки — одно выражение из
                  `core.v_deal_issue`, а не два похожих. Замер 10.09.2026 на
                  01.07-10.09, эпоха managed: 41 / 31 / 16 / 13 / 7 / 0 — плитка
                  и список совпадают во всех шести строках, и так же на трёх
                  других периодах.

                  Колонка суммы ссылкой не сделана: у трёх строк из шести это не
                  сумма по списку, а другая величина — у «депозит больше суммы»
                  это РАЗНИЦА (depositer − сумма сделки), у «выиграна без суммы»
                  сумма депозитов, у остальных сумма сделок. Одна ссылка на всех
                  обещала бы, что под каждой лежит её слагаемое.
                */}
                {attnSort.rows.map((a) => (
                  <tr key={a.issue}>
                    {/* Подпись приезжает с сервера ключом; английская из базы —
                        запасной вариант для вида проблемы, которого ещё нет в словаре. */}
                    <td>{dbLabel(ATTENTION, a.issue, a.label)}</td>
                    <td className="num">
                      <DrillNum
                        enabled={a.deals > 0}
                        drill={{
                          kind: "deals",
                          title: dbLabel(ATTENTION, a.issue, a.label),
                          slice: {
                            dateBasis: "create",
                            /* Ключ приезжает с сервера строкой, и тип его не
                               сторожит. Придумывать здесь проверку не нужно:
                               `api/deals.ts` сверяет `issue` с закрытым списком
                               и отвечает 400 на незнакомый — то есть новый вид
                               расхождения даст видимую ошибку, а не молча
                               пустой фильтр. */
                            issue: a.issue as NonNullable<DealSlice["issue"]>,
                          },
                        }}
                      >
                        {num(a.deals)}
                      </DrillNum>
                    </td>
                    <td className="num">{aed(a.amount_aed_minor)}</td>
                  </tr>
                ))}
                {report.attention.every((a) => a.deals === 0) && (
                  <tr>
                    <td colSpan={3} className="empty">
                      {t(TX.nothingToFix)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.depositOverAmountNote)}
          </div>
        </Card>
      </div>

    </>
  );
}

function MiniStat({
  label,
  value,
  foot,
}: {
  label: string;
  value: string;
  foot?: string;
}): React.ReactElement {
  return (
    <div>
      <div className="metric-label">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      {foot && <div className="metric-foot">{foot}</div>}
    </div>
  );
}

// -------------------------------------------------------------- выгрузка

export function ExportScreen({ filters }: { filters: Filters }): React.ReactElement {
  const [datasets, setDatasets] = useState<ExportDataset[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .exportCatalog()
      .then((r) => setDatasets(r.datasets))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const run = async (key: string, format: "csv" | "xlsx"): Promise<void> => {
    setBusy(`${key}.${format}`);
    setError(null);
    try {
      await api.download(key, format, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Card
        title={t(TX.fullReport)}
        hint={t(TX.everySummaryOneWorkbook)}
        right={
          <button
            className="btn primary"
            disabled={busy !== null}
            onClick={() => void run("report", "xlsx")}
          >
            {busy === "report.xlsx" ? t(TX.preparing) : t(TX.downloadXlsx)}
          </button>
        }
      >
        <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 13 }}>
          {t(TX.fullReportNote)}
        </p>
      </Card>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        {datasets.map((d) => (
          <Card
            key={d.key}
            title={dbLabel(DATASET_TITLE, d.key, d.title)}
            hint={`${d.columns} ${t(TX.columns)}`}
            right={
              <span style={{ display: "flex", gap: 6 }}>
                <button className="btn" disabled={busy !== null} onClick={() => void run(d.key, "csv")}>
                  {busy === `${d.key}.csv` ? "…" : "CSV"}
                </button>
                <button className="btn" disabled={busy !== null} onClick={() => void run(d.key, "xlsx")}>
                  {busy === `${d.key}.xlsx` ? "…" : "XLSX"}
                </button>
              </span>
            }
          >
            <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12 }}>
              {dbLabel(DATASET_NOTE, d.key, d.note)}
            </p>
          </Card>
        ))}
      </div>

      {error && (
        <div className="card err" style={{ marginTop: 14 }}>
          {error}
        </div>
      )}

      <div className="note" style={{ marginTop: 14 }}>
        {t(TX.exportsUseFilters)} {filters.from} → {filters.to}
        {filters.park?.length ? `, ${filters.park.length} ${t(TX.parkS)}` : t(TX.allParks)}.{" "}
        {t(TX.exportMoneyNote)} {t(TX.headersAlwaysEnglish)}
      </div>
    </>
  );
}
